import { useState, useEffect, useRef } from "react";
import React from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useUploadManager } from "@/context/UploadManagerContext";
import RawEditPanel from "@/components/RawEditPanel";
import {
  Eye,
  EyeOff,
  Focus,
  Star,
  Sparkles,
  Loader2,
  Sun,
  Contrast,
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ImageIcon,
  FileImage,
  Download,
  Upload,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Wand2,
  Settings,
  SlidersHorizontal,
  Pencil,
  Trash2,
  Save,
  ArrowLeft,
  Lock,
  XCircle,
  RefreshCw,
  User,
} from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { PreviewBadge, isRawFilename } from "./PreviewBadge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetTrigger,
} from "@/components/ui/sheet";
import { toast } from "sonner";
import axios from "axios";
import DiskUsageBanner from "@/components/DiskUsageBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const imgUrl = (id, type = "original") => `${API}/photos/image/${id}?type=${type}`;

const severityColor = {
  high: "text-red-500",
  medium: "text-amber-600",
  low: "text-primary",
  info: "text-emerald-500",
};

const severityBg = {
  high: "bg-red-500/10 border-red-500/30",
  medium: "bg-amber-500/10 border-amber-500/30",
  low: "bg-primary/10 border-primary/30",
  info: "bg-emerald-500/10 border-emerald-500/30",
};

const XMP_LABELS = {
  "xmp:Rating": "Rating",
  "xmp:Label": "Color Label",
  "xmp:CreateDate": "Date Created",
  "xmp:ModifyDate": "Date Modified",
  "tiff:Make": "Camera Make",
  "tiff:Model": "Camera Model",
  "exif:ExposureTime": "Shutter Speed",
  "exif:FNumber": "Aperture",
  "exif:ISOSpeedRatings": "ISO",
  "exif:FocalLength": "Focal Length",
  "exif:Flash": "Flash",
  "exif:DateTimeOriginal": "Date Original",
  "dc:subject": "Keywords",
  "dc:creator": "Creator",
  "dc:description": "Description",
  "dc:title": "Title",
  "crs:Temperature": "White Balance (K)",
  "crs:Exposure2012": "Exposure",
  "crs:Contrast2012": "Contrast",
  "crs:Highlights2012": "Highlights",
  "crs:Shadows2012": "Shadows",
  "crs:WhiteBalance": "WB Mode",
  "crs:Saturation": "Saturation",
  "crs:Sharpness": "Sharpness",
  "photoshop:DateCreated": "Date Created",
  "aux:Lens": "Lens",
  "aux:LensInfo": "Lens Info",
};

function formatXmpValue(key, val) {
  if (key === "exif:FNumber" && typeof val === "string" && val.includes("/")) {
    const [n, d] = val.split("/").map(Number);
    return d ? `f/${(n / d).toFixed(1)}` : val;
  }
  if (key === "exif:ExposureTime" && typeof val === "string" && val.includes("/")) {
    return `${val}s`;
  }
  if (key === "exif:FocalLength" && typeof val === "string" && val.includes("/")) {
    const [n, d] = val.split("/").map(Number);
    return d ? `${(n / d).toFixed(0)}mm` : val;
  }
  if (key === "crs:Temperature") return `${val}K`;
  if (key === "xmp:Rating") return `${"★".repeat(Number(val))}${"☆".repeat(5 - Number(val))}`;
  if (Array.isArray(val)) return val.join(", ");
  return String(val);
}

function getFaceColor(face) {
  if (face.blink_detected) return "#EF4444";
  if (face.sharpness < 50) return "#F59E0B";
  return "#10B981";
}

function ScoreBar({ score, label }) {
  const color =
    score >= 70 ? "bg-emerald-500" : score >= 40 ? "bg-amber-500" : "bg-red-500";
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-slate-600">{label}</span>
        <span className="font-mono text-slate-900">{score}</span>
      </div>
      <div className="h-1.5 bg-slate-200 rounded-sm overflow-hidden">
        <div
          className={`h-full ${color} score-bar-fill`}
          style={{ "--score-width": `${score}%`, width: `${score}%` }}
        />
      </div>
    </div>
  );
}

function FaceOverlays({ faces }) {
  return faces.map((face, i) => {
    const c = getFaceColor(face);
    const earText = face.left_ear != null && face.right_ear != null
      ? `EAR ${((face.left_ear + face.right_ear) / 2).toFixed(2)}`
      : "";
    const label = face.blink_detected
      ? `BLINK ${earText}`
      : face.confidence
        ? `${(face.confidence * 100).toFixed(0)}% ${earText}`
        : `SHARP ${Math.round(face.sharpness)}`;
    return (
      <div
        key={i}
        className="absolute pointer-events-none"
        style={{
          left: `${face.bbox_pct.x}%`,
          top: `${face.bbox_pct.y}%`,
          width: `${face.bbox_pct.w}%`,
          height: `${face.bbox_pct.h}%`,
          border: `2px solid ${c}`,
        }}
      >
        <span className="face-label" style={{ backgroundColor: c, color: "#000" }}>
          {label}
        </span>
      </div>
    );
  });
}

/* ── Side-by-Side Comparison ──────────────────────────────────── */

function SideBySideCompare({ beforeSrc, afterSrc, beforeLabel, afterLabel }) {
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const handleWheel = (e) => {
    e.preventDefault();
    // Multiplicative zoom for smooth control across the 100%–2000% range
    const factor = e.deltaY > 0 ? 1 / 1.15 : 1.15;
    setZoom((z) => Math.max(1, Math.min(20, z * factor)));
  };
  const handleMouseDown = (e) => {
    // Phase 67: plain drag pans (intuitive default). Shift+drag is
    // reserved for markup — see mkMouseDown below.
    if (zoom <= 1) return;
    if (e.shiftKey) return;  // shift = markup draw (handled elsewhere)
    e.preventDefault();
    setDragging(true);
    setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
  };
  const handleMouseMove = (e) => {
    if (!dragging) return;
    setPan({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  };
  const handleMouseUp = () => setDragging(false);
  const handleDoubleClick = () => {
    if (zoom > 1) { setZoom(1); setPan({ x: 0, y: 0 }); } else { setZoom(3); }
  };

  // Adaptive step: finer steps at low zoom, bigger steps at high zoom
  const stepFor = (z) => (z >= 10 ? 2 : z >= 5 ? 1 : 0.5);

  React.useEffect(() => { setZoom(1); setPan({ x: 0, y: 0 }); }, [beforeSrc, afterSrc]);

  const panelStyle = {
    transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
    transformOrigin: "center center",
    transition: dragging ? "none" : "transform 0.15s ease-out",
  };

  const panelEvents = {
    onWheel: handleWheel,
    onMouseDown: handleMouseDown,
    onMouseMove: handleMouseMove,
    onMouseUp: handleMouseUp,
    onMouseLeave: handleMouseUp,
    onDoubleClick: handleDoubleClick,
  };

  return (
    <div className="w-full space-y-2" data-testid="side-by-side-compare">
      {/* Zoom controls */}
      <div className="flex items-center justify-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => setZoom((z) => Math.max(1, z - stepFor(z)))}
          className="h-7 w-7 p-0 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-sm">
          <ZoomOut className="h-3.5 w-3.5" />
        </Button>
        <span className="font-mono text-[10px] text-slate-500 w-14 text-center">{Math.round(zoom * 100)}%</span>
        <Button variant="ghost" size="sm" onClick={() => setZoom((z) => Math.min(20, z + stepFor(z)))}
          className="h-7 w-7 p-0 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-sm">
          <ZoomIn className="h-3.5 w-3.5" />
        </Button>
        {zoom > 1 && (
          <Button variant="ghost" size="sm" onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }}
            className="h-7 px-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-sm text-[10px]">
            <RotateCcw className="h-3 w-3 mr-1" />Reset
          </Button>
        )}
      </div>

      {/* Side by side panels */}
      <div className="flex gap-1 w-full" style={{ cursor: zoom > 1 ? (dragging ? "grabbing" : "grab") : "zoom-in" }}>
        {/* Before */}
        <div className="flex-1 overflow-hidden rounded-sm border border-slate-200 bg-white relative" {...panelEvents}>
          <div className="absolute top-2 left-2 z-10 bg-black/70 text-white text-[10px] px-2 py-0.5 rounded-sm font-mono">
            {beforeLabel || "Before"}
          </div>
          <div className="flex items-center justify-center overflow-hidden" style={{ maxHeight: "55vh" }}>
            <div style={panelStyle}>
              <img src={beforeSrc} alt="Before" className="max-h-[55vh] w-auto select-none" draggable={false} />
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="w-px bg-amber-500 shrink-0 self-stretch" />

        {/* After */}
        <div className="flex-1 overflow-hidden rounded-sm border border-slate-200 bg-white relative" {...panelEvents}>
          <div className="absolute top-2 right-2 z-10 bg-black/70 text-white text-[10px] px-2 py-0.5 rounded-sm font-mono">
            {afterLabel || "After"}
          </div>
          <div className="flex items-center justify-center overflow-hidden" style={{ maxHeight: "55vh" }}>
            <div style={panelStyle}>
              <img src={afterSrc} alt="After" className="max-h-[55vh] w-auto select-none" draggable={false} />
            </div>
          </div>
        </div>
      </div>
      <p className="text-[9px] text-slate-400 text-center">
        Scroll to zoom both panels (up to 2000%) &middot; Drag to pan &middot; Double-click to toggle 3x
      </p>
    </div>
  );
}

/* ── Compare Switcher: Original / Local Fix / AI Fix ─────────── */

function CompareSwitcher({ photo, imgUrl }) {
  // Available sources for this photo
  const options = React.useMemo(() => {
    const opts = [{ key: "original", label: "Original" }];
    if (photo.edited_path) opts.push({ key: "edited", label: "Local Fix" });
    if (photo.ai_edited_path) opts.push({ key: "ai_edited", label: "AI Fix" });
    return opts;
  }, [photo.edited_path, photo.ai_edited_path]);

  // Default left = Original, right = AI if available else Local
  const initialLeft = "original";
  const initialRight = photo.ai_edited_path ? "ai_edited" : photo.edited_path ? "edited" : "original";
  const [leftKey, setLeftKey] = useState(initialLeft);
  const [rightKey, setRightKey] = useState(initialRight);

  // Reset when photo changes
  React.useEffect(() => {
    setLeftKey("original");
    setRightKey(photo.ai_edited_path ? "ai_edited" : photo.edited_path ? "edited" : "original");
  }, [photo.id, photo.edited_path, photo.ai_edited_path]);

  const labelOf = (k) => options.find((o) => o.key === k)?.label || k;

  const PillGroup = ({ value, onChange, testid }) => (
    <div className="inline-flex gap-1 bg-white border border-slate-200 rounded-sm p-0.5" data-testid={testid}>
      {options.map((o) => (
        <button
          key={o.key}
          onClick={() => onChange(o.key)}
          className={`px-2.5 h-6 text-[10px] uppercase tracking-wider rounded-sm transition-colors ${
            value === o.key
              ? o.key === "ai_edited"
                ? "bg-emerald-500/30 text-emerald-500"
                : o.key === "edited"
                ? "bg-amber-500/30 text-amber-600"
                : "bg-slate-200 text-slate-900"
              : "text-slate-500 hover:text-slate-900"
          }`}
          data-testid={`${testid}-${o.key}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );

  return (
    <div className="w-full space-y-2" data-testid="compare-switcher">
      {/* Side selectors */}
      <div className="flex items-center justify-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-widest text-slate-500">Left</span>
          <PillGroup value={leftKey} onChange={setLeftKey} testid="compare-left" />
        </div>
        <span className="text-slate-400">vs</span>
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-widest text-slate-500">Right</span>
          <PillGroup value={rightKey} onChange={setRightKey} testid="compare-right" />
        </div>
      </div>

      <SideBySideCompare
        beforeSrc={imgUrl(photo.id, leftKey)}
        afterSrc={imgUrl(photo.id, rightKey)}
        beforeLabel={labelOf(leftKey)}
        afterLabel={labelOf(rightKey)}
      />
    </div>
  );
}

/* ── Zoomable Image with Face Overlays ────────────────────────── */

function ZoomableImage({ src, alt, faces }) {
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const containerRef = React.useRef(null);

  const handleWheel = (e) => {
    e.preventDefault();
    // Multiplicative zoom for smooth behaviour across 100%–2000%
    const factor = e.deltaY > 0 ? 1 / 1.15 : 1.15;
    setZoom((z) => Math.max(1, Math.min(20, z * factor)));
  };

  const handleMouseDown = (e) => {
    // Phase 67: plain drag pans while zoomed. Shift+drag is reserved
    // for markup — matches the compare-view behaviour so users don't
    // learn two different gestures.
    if (zoom <= 1 || e.shiftKey) return;
    e.preventDefault();
    setDragging(true);
    setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
  };

  const handleMouseMove = (e) => {
    if (!dragging) return;
    setPan({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  };

  const handleMouseUp = () => setDragging(false);

  const handleDoubleClick = () => {
    if (zoom > 1) {
      setZoom(1);
      setPan({ x: 0, y: 0 });
    } else {
      setZoom(3);
    }
  };

  const resetZoom = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  // Reset zoom when image changes
  React.useEffect(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, [src]);

  // Adaptive step: finer steps at low zoom, bigger at high zoom
  const stepFor = (z) => (z >= 10 ? 2 : z >= 5 ? 1 : 0.5);

  return (
    <div className="flex flex-col items-center gap-2 w-full">
      {/* Zoom controls */}
      <div className="flex items-center gap-1.5" data-testid="zoom-controls">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setZoom((z) => Math.max(1, z - stepFor(z)))}
          className="h-7 w-7 p-0 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-sm"
          data-testid="zoom-out-button"
        >
          <ZoomOut className="h-3.5 w-3.5" />
        </Button>
        <span className="font-mono text-[10px] text-slate-500 w-14 text-center">
          {Math.round(zoom * 100)}%
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setZoom((z) => Math.min(20, z + stepFor(z)))}
          className="h-7 w-7 p-0 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-sm"
          data-testid="zoom-in-button"
        >
          <ZoomIn className="h-3.5 w-3.5" />
        </Button>
        {zoom > 1 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={resetZoom}
            className="h-7 px-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-sm text-[10px]"
            data-testid="zoom-reset-button"
          >
            <RotateCcw className="h-3 w-3 mr-1" />
            Reset
          </Button>
        )}
      </div>

      {/* Zoomable container */}
      <div
        ref={containerRef}
        className="relative overflow-hidden rounded-sm w-full max-h-[55vh] flex items-center justify-center"
        style={{ cursor: zoom > 1 ? (dragging ? "grabbing" : "default") : "zoom-in" }}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onDoubleClick={handleDoubleClick}
        data-testid="zoomable-container"
      >
        <div
          className="relative w-fit transition-transform"
          style={{
            transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
            transformOrigin: "center center",
            transition: dragging ? "none" : "transform 0.15s ease-out",
          }}
        >
          <img
            src={src}
            alt={alt}
            className="max-h-[55vh] w-auto select-none"
            draggable={false}
            data-testid="detail-image"
          />
          <FaceOverlays faces={faces} />
        </div>
      </div>
      {zoom <= 1 && (
        <p className="text-[9px] text-slate-400 mt-0.5">Scroll to zoom (up to 2000%) &middot; Double-click to 3x &middot; Drag to pan</p>
      )}
    </div>
  );
}

/* ── Settings body: provider picker + API keys ───────────────────── */

const PROVIDERS = [
  { key: "gemini",    name: "Gemini",  model: "Gemini 3 Flash",      color: "text-violet-600" },
  { key: "openai",    name: "OpenAI",  model: "GPT-5.2",             color: "text-emerald-500" },
  { key: "anthropic", name: "Claude",  model: "Claude Sonnet 4.5",   color: "text-amber-600" },
];

function SettingsBody({ onClose }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferred, setPreferred] = useState("gemini");
  const [keysSet, setKeysSet] = useState({ gemini: false, openai: false, anthropic: false });
  const [keysMasked, setKeysMasked] = useState({ gemini: "", openai: "", anthropic: "" });
  const [keysInput, setKeysInput] = useState({ gemini: "", openai: "", anthropic: "" });
  const [showKey, setShowKey] = useState({ gemini: false, openai: false, anthropic: false });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/settings`);
        setPreferred(data.preferred_provider || "gemini");
        setKeysSet({
          gemini: data.gemini_key_set,
          openai: data.openai_key_set,
          anthropic: data.anthropic_key_set,
        });
        setKeysMasked({
          gemini: data.gemini_key_masked || "",
          openai: data.openai_key_masked || "",
          anthropic: data.anthropic_key_masked || "",
        });
      } catch {
        toast.error("Could not load settings");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { preferred_provider: preferred };
      // Only include key fields the user actually typed in (so we don't blank out an existing one)
      ["gemini", "openai", "anthropic"].forEach((p) => {
        if (keysInput[p].trim() !== "") {
          payload[`${p}_key`] = keysInput[p].trim();
        }
      });
      const { data } = await axios.put(`${API}/settings`, payload);
      setKeysSet({
        gemini: data.gemini_key_set,
        openai: data.openai_key_set,
        anthropic: data.anthropic_key_set,
      });
      setKeysMasked({
        gemini: data.gemini_key_masked || "",
        openai: data.openai_key_masked || "",
        anthropic: data.anthropic_key_masked || "",
      });
      setKeysInput({ gemini: "", openai: "", anthropic: "" });
      toast.success("Settings saved");
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save settings");
    } finally {
      setSaving(false);
    }
  };

  const clearKey = async (p) => {
    if (!keysSet[p]) return;
    if (!window.confirm(`Clear the ${p} key? The app will fall back to the built-in key.`)) return;
    try {
      const { data } = await axios.put(`${API}/settings`, { [`${p}_key`]: "" });
      setKeysSet({
        gemini: data.gemini_key_set,
        openai: data.openai_key_set,
        anthropic: data.anthropic_key_set,
      });
      setKeysMasked({
        gemini: data.gemini_key_masked || "",
        openai: data.openai_key_masked || "",
        anthropic: data.anthropic_key_masked || "",
      });
      toast.success(`${p} key cleared`);
    } catch {
      toast.error("Could not clear key");
    }
  };

  if (loading) {
    return (
      <div className="py-10 flex items-center justify-center" data-testid="settings-loading">
        <Loader2 className="h-5 w-5 animate-spin text-slate-500" />
      </div>
    );
  }

  return (
    <div className="space-y-5 py-1">
      {/* Preferred provider */}
      <div>
        <label className="text-[10px] uppercase tracking-[0.2em] font-medium text-slate-500 mb-2 block">
          Preferred AI provider
        </label>
        <div className="grid grid-cols-3 gap-2" data-testid="provider-picker">
          {PROVIDERS.map((p) => (
            <button
              key={p.key}
              onClick={() => setPreferred(p.key)}
              className={`p-2.5 rounded-sm border text-left transition-colors ${
                preferred === p.key
                  ? "border-slate-200 bg-slate-100"
                  : "border-slate-200 hover:border-slate-300"
              }`}
              data-testid={`provider-${p.key}`}
            >
              <div className={`text-xs font-medium ${p.color}`}>{p.name}</div>
              <div className="text-[10px] text-slate-500 mt-0.5 font-mono">{p.model}</div>
            </button>
          ))}
        </div>
        <p className="text-[10px] text-slate-400 mt-1.5">
          Used for AI Detect, AI Group, and the audit pass during AI Fix.
          AI Fix's image generation always uses Gemini Nano Banana
          (only provider supporting reference-based face regeneration).
        </p>
      </div>

      {/* API Keys */}
      <div className="space-y-3">
        <label className="text-[10px] uppercase tracking-[0.2em] font-medium text-slate-500 block">
          API Keys (optional)
        </label>
        {PROVIDERS.map((p) => (
          <div key={p.key} className="space-y-1" data-testid={`key-row-${p.key}`}>
            <div className="flex items-center justify-between">
              <span className={`text-[11px] font-medium ${p.color}`}>{p.name} key</span>
              {keysSet[p.key] && (
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[10px] text-slate-500">{keysMasked[p.key]}</span>
                  <button
                    onClick={() => clearKey(p.key)}
                    className="text-[10px] text-red-500 hover:underline"
                    data-testid={`clear-key-${p.key}`}
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>
            <div className="flex gap-1">
              <Input
                type={showKey[p.key] ? "text" : "password"}
                placeholder={
                  keysSet[p.key] ? "Type a new key to replace" : `Paste ${p.name} key here…`
                }
                value={keysInput[p.key]}
                onChange={(e) => setKeysInput({ ...keysInput, [p.key]: e.target.value })}
                className="bg-white border-slate-200 text-slate-900 rounded-sm h-8 text-xs placeholder:text-slate-400 flex-1"
                data-testid={`key-input-${p.key}`}
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowKey({ ...showKey, [p.key]: !showKey[p.key] })}
                className="h-8 px-2 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-sm"
                data-testid={`toggle-key-${p.key}`}
                aria-label={showKey[p.key] ? "Hide key" : "Show key"}
              >
                {showKey[p.key] ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              </Button>
            </div>
          </div>
        ))}
        <p className="text-[10px] text-slate-400">
          Empty key fields use the built-in fallback (Emergent universal key for OpenAI/Claude,
          server-side <code>GEMINI_API_KEY</code> for Gemini).
        </p>
      </div>

      <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
        <Button
          variant="outline"
          size="sm"
          onClick={onClose}
          className="border-slate-200 text-slate-600 hover:bg-slate-100 rounded-sm h-8 text-xs"
          data-testid="settings-cancel-button"
        >
          Cancel
        </Button>
        <Button
          size="sm"
          disabled={saving}
          onClick={save}
          className="bg-white text-black hover:bg-gray-200 rounded-sm h-8 text-xs"
          data-testid="settings-save-button"
        >
          {saving ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Saving…</> : "Save"}
        </Button>
      </div>
    </div>
  );
}

/* ── Per-Face Layer Editor ─────────────────────────────────────── */
/* User clicks a face overlay → choose Local or AI → a transparent
   PNG of just-that-face is generated and stacked as a toggleable layer. */

const ADJ_DEFAULTS = { brightness: 1.0, exposure: 0.0, sharpness: 1.0 };

/**
 * Step 1 auto-fix: compute initial slider values from the photo's existing
 * analysis (mean luminance + Laplacian sharpness variance from
 * photo_analyzer). The user can fine-tune from there; Reset returns the
 * sliders to these auto-fixed values, not neutral.
 *
 * Phase 57 calibration: target luminance dropped from 130 → 118 and the
 * "no touch" window widened to [100..160] (was [110..165]). The old
 * target sat above natural mid-gray (~118 on most calibrated monitors)
 * so auto-fixed photos looked cheerful-but-blown-out. Max positive
 * exposure boost also capped at +0.6 EV (was +1.0 EV) — aggressive
 * lifts on already-mid photos were the main "over-exposed" complaint.
 */
function computeAutoAdjustments(photo) {
  const lum = typeof photo?.brightness === "number" ? photo.brightness : 118;
  const sharp = typeof photo?.overall_sharpness === "number"
    ? photo.overall_sharpness : 150;

  // Target ~118 on lift, ~128 on pull-down — asymmetric because the eye
  // tolerates slight underexposure much better than slight overexposure.
  let exposure = 0;
  if (lum < 100) {
    // Cap the lift at +0.6 EV so a very dark scene doesn't get pushed
    // into blown-highlight territory. The remaining underexposure is
    // still visible enough to see faces and can be nudged manually.
    exposure = Math.min(0.6, Math.log2(118 / Math.max(50, lum)));
  } else if (lum > 160) {
    // Pull-down toward 128 for bright scenes. Negative cap of -1.0 EV
    // stays as before — pulling down never risks blown highlights.
    exposure = Math.max(-1.0, Math.log2(128 / Math.max(128, lum)));
  }
  // Snap to the slider's 0.05 step
  exposure = Math.round(exposure / 0.05) * 0.05;

  // Sharpness boost based on Laplacian variance (typical 50–500 for JPEGs).
  let sharpness = 1.0;
  if (sharp < 70) sharpness = 1.8;
  else if (sharp < 120) sharpness = 1.4;
  else if (sharp < 200) sharpness = 1.2;

  // Brightness multiplier left at 1.0 — exposure handles the luminance shift,
  // and stacking both would over-correct.
  return { brightness: 1.0, exposure, sharpness };
}

function ClickableFaceOverlays({ faces, onPick, hidden, issuesOnly = false, dismissedIdxSet }) {
  if (hidden) return null;
  const dismissed = dismissedIdxSet || new Set();
  return faces.map((face, i) => {
    const hasFlare = !!face.has_glasses_flare;
    const isIssue = !!face.blink_detected || hasFlare;
    // Faces the admin has already marked "acceptable" should not
    // continue to render as red boxes — visually removes the flag
    // as soon as the admin dismisses it.
    const isDismissed = dismissed.has(i);
    // "issues" mode: skip clean faces entirely so the Working pane
    // only calls attention to red / amber problems (default UX).
    if (issuesOnly && (!isIssue || isDismissed)) return null;
    const borderColor = (face.blink_detected && !isDismissed)
      ? "#EF4444"
      : (hasFlare && !isDismissed)
      ? "#F59E0B"
      : "#10B981";
    return (
      <button
        key={i}
        onClick={(e) => {
          e.stopPropagation();
          onPick(i, e);
        }}
        onMouseDown={(e) => e.stopPropagation()}
        className="absolute group cursor-pointer pointer-events-auto"
        style={{
          left: `${face.bbox_pct.x}%`,
          top: `${face.bbox_pct.y}%`,
          width: `${face.bbox_pct.w}%`,
          height: `${face.bbox_pct.h}%`,
          border: `2px solid ${borderColor}`,
          background: "transparent",
          padding: 0,
        }}
        data-testid={`clickable-face-${i}`}
        title={`Face ${i + 1}${face.blink_detected ? " · BLINK" : ""}${
          hasFlare ? " · GLASSES FLARE" : ""
        } — click to add a fix layer`}
      >
        <span className="absolute inset-0 bg-white/0 group-hover:bg-white/15 transition-colors" />
        {hasFlare && (
          <span
            className="absolute -top-1.5 -right-1.5 bg-amber-500 text-black text-[9px] font-bold rounded-sm px-1 py-0.5 leading-none pointer-events-none shadow-md"
            data-testid={`glasses-flare-badge-${i}`}
          >
            FLARE
          </span>
        )}
      </button>
    );
  });
}

/* ── MarkupListPanel ────────────────────────────────────────────────
 * Right-sidebar tab that mirrors the drawn markup boxes as an editable
 * list. Each row lets the admin rename the label, add an instruction
 * for the editor (persisted as ``note``), see which faces the box
 * covers, and delete it. Click a row to highlight the corresponding
 * rectangle on the canvas (``activeIdx``).
 */
function MarkupListPanel({
  markupBoxes = [], activeIdx, setActiveIdx,
  showMarkup, onToggleMarkup,
  onUpdate, onDelete, onClearAll,
  photoFilename,
  // Phase 61: commit-for-review integration.
  onCommitMarkup, isCommitted = false, committedInfo,
}) {
  const total = markupBoxes.length;
  const manualCount = markupBoxes.filter((b) => b.source !== "auto").length;
  const autoCount = total - manualCount;

  const updateField = (idx, patch) => {
    const next = markupBoxes.map((b, i) => (i === idx ? { ...b, ...patch } : b));
    onUpdate && onUpdate(next);
  };

  return (
    <div className="space-y-3" data-testid="markup-list-inner">
      {/* Header + Draw toggle ------------------------------------- */}
      <div className="bg-white border border-slate-200 rounded-sm p-3 space-y-2">
        <div className="flex items-center gap-2">
          <span className="bg-accent text-white text-[10px] font-mono font-medium px-1.5 py-0.5 rounded-sm">
            MARKUP
          </span>
          <h4 className="text-[10px] uppercase tracking-[0.2em] font-medium text-slate-600">
            Areas for editor
          </h4>
        </div>
        <p className="text-[10px] text-slate-500 leading-snug">
          Draw rectangles on the working pane to tell the editor which
          areas need attention. Each row below becomes an instruction
          in the downloaded ZIP.
        </p>
        <div className="flex gap-1.5">
          <Button
            variant={showMarkup ? "default" : "outline"}
            size="sm"
            onClick={onToggleMarkup}
            className={`flex-1 h-8 rounded-sm text-[11px] font-medium ${
              showMarkup
                ? "bg-accent text-white hover:bg-accent/90"
                : "border-accent/40 text-accent hover:bg-accent/10"
            }`}
            data-testid="markup-draw-toggle"
          >
            <Pencil className="h-3 w-3 mr-1.5" />
            {showMarkup ? "Drawing… click to pause" : "Draw on working pane"}
          </Button>
        </div>

        {/* Phase 61: Commit markups — signs off the current areas so the
            editor knows this photo is ready to pick up. Any edit to the
            box list clears the commit automatically (backend + frontend
            both drop the flag on PUT / DELETE). */}
        {onCommitMarkup && (
          <div className="pt-2 border-t border-slate-100">
            {isCommitted ? (
              <div
                className="w-full flex items-center gap-2 px-2.5 py-1.5 bg-emerald-50 border border-emerald-200 rounded-sm text-emerald-700"
                data-testid="markup-committed-badge"
                title={committedInfo?.at ? `Committed ${new Date(committedInfo.at).toLocaleString()}` : "Committed"}
              >
                <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                <span className="text-[11px] font-bold uppercase tracking-widest">
                  Committed for editor
                </span>
                {committedInfo?.at && (
                  <span className="ml-auto text-[10px] font-mono opacity-70">
                    {new Date(committedInfo.at).toLocaleDateString()}
                  </span>
                )}
              </div>
            ) : (
              <Button
                type="button"
                onClick={onCommitMarkup}
                disabled={total === 0}
                className="w-full h-8 rounded-sm text-[11px] font-bold uppercase tracking-widest bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50 disabled:bg-slate-300 disabled:cursor-not-allowed"
                data-testid="markup-commit-btn"
                title={
                  total === 0
                    ? "Draw at least one area first, then commit"
                    : "Freeze these markups as ready-for-editor. Any later edit will require a fresh commit."
                }
              >
                <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                Commit {total} markup{total === 1 ? "" : "s"} for editor
              </Button>
            )}
          </div>
        )}
        <div className="flex items-center justify-between text-[10px] text-slate-500">
          <span>
            <span className="font-mono text-slate-900">{total}</span>
            {" area"}{total === 1 ? "" : "s"}
            {autoCount > 0 && (
              <span className="text-amber-600 ml-1">
                · {autoCount} auto
              </span>
            )}
          </span>
          {total > 0 && (
            <button
              type="button"
              onClick={onClearAll}
              className="inline-flex items-center gap-1 rounded-sm border border-red-300 bg-red-50 hover:bg-red-100 hover:border-red-500 hover:text-red-700 text-red-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest transition-colors"
              data-testid="markup-clear-all"
              title="Remove every markup box on this photo"
            >
              <Trash2 className="h-3 w-3" />
              Reject all
            </button>
          )}
        </div>
      </div>

      {/* List --------------------------------------------------- */}
      {total === 0 ? (
        <div
          className="bg-slate-50 border border-dashed border-slate-300 rounded-sm p-4 text-center"
          data-testid="markup-list-empty"
        >
          <Pencil className="h-4 w-4 text-slate-300 mx-auto mb-2" />
          <p className="text-[11px] text-slate-500 leading-snug">
            No markup on <span className="font-mono">{photoFilename || "this photo"}</span>
            {isRawFilename(photoFilename) && (
              <span
                className="ml-1.5 inline-block rounded-sm bg-amber-100 text-amber-900 border border-amber-300 px-1 py-[1px] text-[8px] font-semibold uppercase tracking-wider align-middle"
                title="Preview only — editor downloads the original RAW for retouching"
              >
                Preview
              </span>
            )}
            {" "}yet.
            <br />
            Turn on <span className="font-medium text-slate-700">Draw</span> and click-drag
            rectangles on the working pane.
          </p>
        </div>
      ) : (
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-[40vh] overflow-y-auto pr-1"
          data-testid="markup-list-items"
        >
          {markupBoxes.map((b, i) => {
            const isAuto = b.source === "auto";
            const isActive = activeIdx === i;
            return (
              <div
                key={i}
                onClick={() => setActiveIdx(i)}
                className={`bg-white border rounded-sm p-2 space-y-1.5 cursor-pointer transition-colors ${
                  isActive
                    ? "border-accent shadow-sm ring-1 ring-accent/30"
                    : "border-slate-200 hover:border-slate-300"
                }`}
                data-testid={`markup-list-item-${i}`}
              >
                <div className="flex items-center gap-1.5">
                  <span
                    className={`text-[9px] font-mono font-bold px-1 py-0.5 rounded-sm ${
                      isAuto ? "bg-amber-500 text-black" : "bg-accent text-white"
                    }`}
                  >
                    {isAuto ? "AUTO" : `#${i + 1}`}
                  </span>
                  <input
                    type="text"
                    value={b.label || ""}
                    onChange={(e) => updateField(i, { label: e.target.value })}
                    onClick={(e) => e.stopPropagation()}
                    placeholder={`Area ${i + 1}`}
                    className="flex-1 text-[11px] font-medium bg-transparent border-0 focus:outline-none focus:ring-1 focus:ring-accent/40 rounded-sm px-1 min-w-0"
                    data-testid={`markup-item-label-${i}`}
                  />
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onDelete && onDelete(i); }}
                    className="p-0.5 text-slate-400 hover:text-red-600"
                    title={isAuto
                      ? "Dismiss — this face is fine, don't send to editor"
                      : "Delete this markup"}
                    data-testid={`markup-item-delete-${i}`}
                  >
                    <XCircle className="h-3.5 w-3.5" />
                  </button>
                </div>
                <textarea
                  value={b.note || ""}
                  onChange={(e) => updateField(i, { note: e.target.value })}
                  onClick={(e) => e.stopPropagation()}
                  placeholder="Instruction for editor…"
                  rows={2}
                  className="w-full text-[10px] bg-slate-50 border border-slate-200 rounded-sm p-1.5 resize-none focus:outline-none focus:ring-1 focus:ring-accent/40"
                  data-testid={`markup-item-note-${i}`}
                />
                <div className="flex items-center gap-2 text-[9px] font-mono text-slate-400">
                  <span>
                    x <span className="text-slate-600">{Math.round((b.x || 0) * 100)}%</span>
                  </span>
                  <span>
                    y <span className="text-slate-600">{Math.round((b.y || 0) * 100)}%</span>
                  </span>
                  <span>
                    w <span className="text-slate-600">{Math.round((b.w || 0) * 100)}%</span>
                  </span>
                  <span>
                    h <span className="text-slate-600">{Math.round((b.h || 0) * 100)}%</span>
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function FaceLayerEditor({
  photo, sessionId, refreshPhoto, imgUrl,
  canFixFaces = true,
  // Group siblings + markup props — Edit tab is the single-screen hub.
  groupPhotos = [], jobId = null,
  markupBoxes = [], onSaveMarkup, onClearMarkup,
  // Phase 61: commit markups + committed status.
  onCommitMarkup, isCommitted = false, committedInfo,
  // Phase 64: quick "Set as Best Photo" shortcut (parent handles the
  // confirm + markup-cleanup dance since it owns both bits of state).
  onSetBestPhoto,
}) {
  const [hiddenIds, setHiddenIds] = useState(() => new Set());
  // 3-state face box visibility: "issues" (default, red-only) → "all"
  // (red + green) → "off". Cycled by the "Faces" toolbar button.
  const [faceBoxMode, setFaceBoxMode] = useState("issues");
  const showFaceBoxes = faceBoxMode !== "off";

  // Auto-populate the Markup panel from every red-flagged face on this
  // photo, unless the admin already dismissed them (persisted on the
  // photo doc as ``dismissed_face_indices``). Runs on photo change and
  // whenever we land with an empty markup list — no sessionStorage
  // guard, so a fresh browser session still gets the issues surfaced.
  useEffect(() => {
    if (!onSaveMarkup) return; // read-only viewer (editor role)
    if ((markupBoxes || []).length > 0) return;
    const dismissed = new Set(photo.dismissed_face_indices || []);
    const auto = _autoBoxesFromFaces(photo.faces, dismissed);
    if (auto.length === 0) return;
    onSaveMarkup(auto);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photo.id, photo.faces, photo.dismissed_face_indices, markupBoxes.length]);
  // Group photo compare: cycler over sibling photos in the same group.
  const [originalPhotoId, setOriginalPhotoId] = useState(photo.id);
  // Loading state for the left (Original) pane image — needed to show
  // a spinner + label while the CR2 → JPEG preview is transcoded and
  // sent over the wire (can take a couple of seconds on the first hit).
  const [originalLoading, setOriginalLoading] = useState(false);
  useEffect(() => { setOriginalPhotoId(photo.id); }, [photo.id]);
  const originalPhoto = React.useMemo(
    () => groupPhotos.find((p) => p.id === originalPhotoId) || photo,
    [groupPhotos, originalPhotoId, photo],
  );
  // Start the spinner whenever the target originalPhotoId changes.
  // Cleared by the <img>'s onLoad handler.
  useEffect(() => { setOriginalLoading(true); }, [originalPhotoId]);
  const groupIdx = React.useMemo(
    () => groupPhotos.findIndex((p) => p.id === originalPhotoId),
    [groupPhotos, originalPhotoId],
  );
  const cycleOriginal = (delta) => {
    if (groupPhotos.length < 2) return;
    const n = groupPhotos.length;
    const nextIdx = ((groupIdx < 0 ? 0 : groupIdx) + delta + n) % n;
    setOriginalPhotoId(groupPhotos[nextIdx].id);
  };

  // Markup overlay — toggled from the Edit toolbar. When on, boxes are
  // drawn on top of the Working pane so admin can annotate while
  // working on face layers. Draw is click-drag (crosshair cursor);
  // Shift+drag still pans while zoomed.
  // Markup mode is ON by default so admins can start drawing on the
  // Working pane the moment the Edit view opens — the sidebar Markup
  // tab is also the default (see ``sidebarTab`` below).
  const [showMarkup, setShowMarkup] = useState(true);
  const [mkDrawing, setMkDrawing] = useState(false);
  const mkDrawStartRef = React.useRef(null);
  const [mkTempBox, setMkTempBox] = useState(null);
  const [mkActiveIdx, setMkActiveIdx] = useState(null);

  // ── Clone tool ────────────────────────────────────────────────
  // Two-step interaction: (1) drag on the LEFT pane to pick a source
  // rectangle, (2) click on the RIGHT pane to place. Both coord sets
  // stored as normalized fractions of their respective natural images.
  // Step 1 auto-fix: sliders open at the AI-computed corrected values, not
  // neutral 1.0 / 0 / 1.0. Reset returns to these.
  const autoAdj = React.useMemo(() => computeAutoAdjustments(photo), [photo.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const [adj, setAdj] = useState(autoAdj);

  // Right-sidebar tab. Auto-switches to "markup" when the admin toggles the
  // Markup tool so the two panes (canvas + list) stay in sync.
  const [sidebarTab, setSidebarTab] = useState("markup");
  useEffect(() => {
    if (showMarkup) setSidebarTab("markup");
  }, [showMarkup]);
  // Re-init when switching photos
  useEffect(() => { setAdj(autoAdj); }, [autoAdj]);
  const [picker, setPicker] = useState(null); // { faceIndex, x, y }
  const [creating, setCreating] = useState(false);
  const [merging, setMerging] = useState(false);

  // Zoom + pan state (matches the Compare view UX)
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStartRef = React.useRef({ x: 0, y: 0 });

  const containerRef = React.useRef(null);

  const layers = React.useMemo(() => photo.face_layers || [], [photo.face_layers]);
  const visibleIds = React.useMemo(
    () => layers.filter((L) => L.status === "complete" && !hiddenIds.has(L.id)).map((L) => L.id),
    [layers, hiddenIds]
  );

  // Auto-poll while any layer is processing
  useEffect(() => {
    const anyProcessing = layers.some((L) => L.status === "processing");
    if (!anyProcessing) return;
    const t = setInterval(() => refreshPhoto(), 3000);
    return () => clearInterval(t);
  }, [layers, refreshPhoto]);

  // Reset zoom when switching photos
  useEffect(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setPicker(null);
  }, [photo.id]);

  const stepFor = (z) => (z >= 10 ? 2 : z >= 5 ? 1 : 0.5);

  const handleWheel = (e) => {
    e.preventDefault();
    const factor = e.deltaY > 0 ? 1 / 1.15 : 1.15;
    setZoom((z) => Math.max(1, Math.min(20, z * factor)));
  };
  const handleMouseDown = (e) => {
    // Phase 67: plain drag pans while zoomed. Shift+drag is reserved
    // for markup drawing on the Working pane.
    if (zoom <= 1 || e.shiftKey) return;
    e.preventDefault();
    setDragging(true);
    dragStartRef.current = { x: e.clientX - pan.x, y: e.clientY - pan.y };
    setPicker(null);
  };
  const handleMouseMove = (e) => {
    if (!dragging) return;
    setPan({ x: e.clientX - dragStartRef.current.x, y: e.clientY - dragStartRef.current.y });
  };
  const handleMouseUp = () => setDragging(false);
  const handleDoubleClick = () => {
    if (zoom > 1) { setZoom(1); setPan({ x: 0, y: 0 }); } else { setZoom(3); }
  };

  const toggle = (id) =>
    setHiddenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/photos/${photo.id}/face-layer/${id}`);
      toast.success("Layer removed");
      setHiddenIds((p) => { const n = new Set(p); n.delete(id); return n; });
      refreshPhoto();
    } catch {
      toast.error("Could not remove layer");
    }
  };

  const create = async (faceIndex, fixType) => {
    setPicker(null);
    setCreating(true);
    try {
      const { data } = await axios.post(
        `${API}/photos/${photo.id}/face-layer`,
        { face_index: faceIndex, fix_type: fixType },
      );
      const label = fixType === "ai"
        ? "AI"
        : fixType === "glasses_flare"
        ? "Glasses Flare"
        : "Local";
      toast.info(`${label} layer for Face ${faceIndex + 1} started`);
      // Visible by default — make sure it's not hidden
      setHiddenIds((p) => { const n = new Set(p); n.delete(data.id); return n; });
      await refreshPhoto();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start fix");
    } finally {
      setCreating(false);
    }
  };

  // Remove the AUTO markup for a face — admin confirms the face is fine
  // and this face should NOT be sent to the editor for a fix.
  //   1. Remove the AUTO markup row (immediate local list update).
  //   2. POST /dismiss-face → persist in ``dismissed_face_indices`` +
  //      audit log so the flag stays gone across reloads.
  //   3. Refresh the photo doc so ``ClickableFaceOverlays`` re-renders
  //      without the red box on the working pane.
  const removeAutoForFace = async (faceIndex) => {
    setPicker(null);
    const next = (markupBoxes || []).filter(
      (b) => !(b.source === "auto" && b.face_index === faceIndex),
    );
    if (next.length !== (markupBoxes || []).length) {
      onSaveMarkup && onSaveMarkup(next);
    }
    try {
      await axios.post(
        `${API}/photos/${photo.id}/dismiss-face`,
        { face_index: faceIndex },
      );
      await refreshPhoto();
    } catch (err) {
      /* eslint-disable-next-line no-console */
      console.warn("Could not persist face dismissal", err);
    }
    toast.success(`Face ${faceIndex + 1} marked acceptable`);
  };

  // Accept the AUTO markup for a face — admin confirms this face IS a
  // problem and should be fixed by the editor. In practice the AUTO
  // markup is already in the list by default, so we just tag it as
  // admin-confirmed (source: "manual") so the editor sees it as a
  // reviewed instruction, not a raw detection.
  const acceptAutoForFace = (faceIndex) => {
    setPicker(null);
    const next = (markupBoxes || []).map((b) => {
      if (b.source === "auto" && b.face_index === faceIndex) {
        return { ...b, source: "manual", note: b.note || "Admin confirmed" };
      }
      return b;
    });
    onSaveMarkup && onSaveMarkup(next);
    toast.info(`Face ${faceIndex + 1} confirmed for editor`);
  };
  // Back-compat alias — the popover still calls this name.
  const dismissAutoForFace = removeAutoForFace;

  const merge = async () => {
    setMerging(true);
    try {
      const res = await axios.post(
        `${API}/photos/${photo.id}/merge-download`,
        { visible_layer_ids: visibleIds, adjustments: adj },
        { responseType: "blob", timeout: 120000 },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      const stem = (photo.filename || "photo").replace(/\.[^.]+$/, "");
      a.href = url;
      a.download = `${stem}_merged.jpg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Merged photo downloaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Merge download failed");
    } finally {
      setMerging(false);
    }
  };

  const filterCss = `brightness(${adj.brightness}) brightness(${Math.pow(2, adj.exposure)})`;
  const transformCss = {
    transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
    transformOrigin: "center center",
    transition: dragging ? "none" : "transform 0.15s ease-out",
  };

  // ── Markup drawing on the Working pane ─────────────────────────
  // Coordinates are normalized fractions [0,1] of the natural image.
  // Screen-space cursor deltas are divided by the SCALED bounding
  // rect so drawing works precisely at any zoom (see MarkupEditor for
  // the same pattern).
  const workingImgRef = React.useRef(null);
  const mkMouseDown = (e) => {
    if (!showMarkup || !workingImgRef.current) return;
    // Phase 67: markup uses SHIFT+drag to draw. Plain drag falls
    // through to the parent's pan handler — much more intuitive when
    // the primary action while zoomed is "look around", not "draw a
    // rectangle by accident".
    if (!e.shiftKey) return;
    if (e.button !== 0) return;
    const ir = workingImgRef.current.getBoundingClientRect();
    if (ir.width <= 0 || ir.height <= 0) return;
    const nx = (e.clientX - ir.left) / ir.width;
    const ny = (e.clientY - ir.top) / ir.height;
    if (nx < 0 || nx > 1 || ny < 0 || ny > 1) return;
    setMkDrawing(true);
    mkDrawStartRef.current = { nx, ny };
    setMkTempBox({ x: nx, y: ny, w: 0, h: 0 });
    setMkActiveIdx(null);
    e.stopPropagation();
    e.preventDefault();
  };
  const mkMouseMove = (e) => {
    if (!mkDrawing || !mkDrawStartRef.current || !workingImgRef.current) return;
    const ir = workingImgRef.current.getBoundingClientRect();
    const nx = Math.max(0, Math.min(1, (e.clientX - ir.left) / ir.width));
    const ny = Math.max(0, Math.min(1, (e.clientY - ir.top) / ir.height));
    setMkTempBox({
      x: Math.min(mkDrawStartRef.current.nx, nx),
      y: Math.min(mkDrawStartRef.current.ny, ny),
      w: Math.abs(nx - mkDrawStartRef.current.nx),
      h: Math.abs(ny - mkDrawStartRef.current.ny),
    });
    e.stopPropagation();
  };
  const mkMouseUp = () => {
    if (!mkDrawing) return;
    setMkDrawing(false);
    const b = mkTempBox;
    mkDrawStartRef.current = null;
    setMkTempBox(null);
    if (!b || b.w < 0.005 || b.h < 0.005) return;
    const next = [
      ...markupBoxes,
      { x: b.x, y: b.y, w: b.w, h: b.h,
        label: `Area ${markupBoxes.length + 1}`, note: "", source: "manual" },
    ];
    setMkActiveIdx(next.length - 1);
    onSaveMarkup && onSaveMarkup(next);
  };
  const mkDelete = (i) => {
    const next = markupBoxes.filter((_, idx) => idx !== i);
    setMkActiveIdx(null);
    onSaveMarkup && onSaveMarkup(next);
  };
  const mkClearAll = () => {
    if (markupBoxes.length === 0) return;
    if (!window.confirm("Clear all markups on this photo?")) return;
    setMkActiveIdx(null);
    onClearMarkup && onClearMarkup();
  };

  return (
    <div className="w-full flex flex-col gap-4" data-testid="face-layer-editor">
      {/* Top: image + clickable face boxes (side-by-side Original + Working) */}
      <div className="w-full min-w-0 space-y-2">
        {/* Phase 62: toolbar reorganised into 3 sections so controls
            live NEAR the pane they affect:
              • LEFT  — group cycler (swaps the LEFT/Original pane)
              • CENTER — zoom + face-box mode (both panes)
              • RIGHT — Markup toggle (draws on the RIGHT/Working pane)
        */}
        <div className="flex items-center justify-between gap-2">
          {/* ── LEFT: group cycler ────────────────────────────────── */}
          <div className="flex items-center gap-2 min-w-0">
            {groupPhotos.length > 1 ? (
              <div className="flex items-center gap-0.5 bg-white border border-slate-200 rounded-sm h-7"
                   data-testid="group-cycler">
                <Button variant="ghost" size="icon"
                  onClick={() => cycleOriginal(-1)}
                  className="h-7 w-6 p-0 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-sm"
                  data-testid="group-cycler-prev">
                  <ChevronLeft className="h-3.5 w-3.5" />
                </Button>
                <span className="text-[10px] font-mono text-slate-700 px-2 truncate max-w-[220px]"
                      title={originalPhoto.filename}>
                  {originalPhoto.filename}
                  <span className="text-slate-400 ml-1">
                    ({(groupIdx < 0 ? 0 : groupIdx) + 1}/{groupPhotos.length})
                  </span>
                </span>
                <PreviewBadge filename={originalPhoto.filename} className="mr-1" />
                <Button variant="ghost" size="icon"
                  onClick={() => cycleOriginal(1)}
                  className="h-7 w-6 p-0 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-sm"
                  data-testid="group-cycler-next">
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            ) : (
              <span className="text-[10px] font-mono text-slate-400 truncate max-w-[220px]"
                    title={originalPhoto.filename}>
                {originalPhoto.filename}
              </span>
            )}
            {/* Phase 72: Star button on the LEFT pane. The Right pane
                is anchored to the current Best (``photo``), so the
                user needs a way to promote a candidate they find
                while stepping through the group on the LEFT
                (``originalPhoto``) — without having to swap the
                Right pane out from under themselves and losing the
                comparison view. Only offered when the LEFT is NOT
                already the Best. */}
            {onSetBestPhoto && originalPhoto && !originalPhoto.is_best_shot
              && originalPhoto.id !== photo.id && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onSetBestPhoto(originalPhoto)}
                className="h-7 px-2 rounded-sm text-[10px] text-amber-600 hover:bg-amber-50 border border-amber-200"
                data-testid="left-pane-set-best"
                title={`Promote ${originalPhoto.filename} to Best of the group. Current Best drops to 2nd rank; any markups drawn on the old Best are cleared.`}
              >
                <Star className="h-3 w-3 mr-1 fill-current" />
                Make Best
              </Button>
            )}
          </div>

          {/* ── CENTER: zoom + face-box mode ─────────────────────── */}
          <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm"
            onClick={() => setZoom((z) => Math.max(1, z - stepFor(z)))}
            className="h-7 w-7 p-0 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-sm"
            data-testid="layers-zoom-out">
            <ZoomOut className="h-3.5 w-3.5" />
          </Button>
          <span className="font-mono text-[10px] text-slate-500 w-14 text-center">
            {Math.round(zoom * 100)}%
          </span>
          <Button variant="ghost" size="sm"
            onClick={() => setZoom((z) => Math.min(20, z + stepFor(z)))}
            className="h-7 w-7 p-0 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-sm"
            data-testid="layers-zoom-in">
            <ZoomIn className="h-3.5 w-3.5" />
          </Button>
          {zoom > 1 && (
            <Button variant="ghost" size="sm"
              onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }}
              className="h-7 px-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-sm text-[10px]"
              data-testid="layers-zoom-reset">
              <RotateCcw className="h-3 w-3 mr-1" />Reset
            </Button>
          )}
          <span className="w-px h-4 bg-slate-200 mx-1" />
          <Button variant="ghost" size="sm"
            onClick={() => {
              // Cycle: issues → all → off → issues …
              setFaceBoxMode((m) => m === "issues" ? "all" : m === "all" ? "off" : "issues");
              setPicker(null);
            }}
            className={`h-7 px-2 rounded-sm text-[10px] ${
              faceBoxMode === "issues"
                ? "text-red-500 hover:bg-red-500/10"
                : faceBoxMode === "all"
                  ? "text-emerald-500 hover:bg-emerald-500/10"
                  : "text-slate-500 hover:text-slate-900 hover:bg-slate-100"
            }`}
            data-testid="toggle-face-boxes"
            title={
              faceBoxMode === "issues"
                ? "Currently: RED (issues) only — click to show ALL"
                : faceBoxMode === "all"
                  ? "Currently: ALL boxes — click to HIDE"
                  : "Currently: HIDDEN — click to show issues"
            }
          >
            {faceBoxMode === "off" ? <EyeOff className="h-3 w-3 mr-1" /> : <Eye className="h-3 w-3 mr-1" />}
            Faces {faceBoxMode === "issues" ? "· Issues" : faceBoxMode === "all" ? "· All" : "· Off"}
          </Button>
          </div>

          {/* ── RIGHT: Markup toggle + compact slider strip (Working pane) ── */}
          <div className="flex items-center gap-2 min-w-0 justify-end">
          {/* Markup toggle — folds the old Markup tab into Edit. Boxes
              overlay the Working (right) pane; click-drag draws;
              Shift+drag still pans while zoomed. */}
          {jobId && (
            <Button variant="ghost" size="sm"
              onClick={() => { setShowMarkup((v) => !v); setPicker(null); setMkActiveIdx(null); }}
              className={`h-7 px-2 rounded-sm text-[10px] ${
                showMarkup
                  ? "text-accent hover:bg-accent/10"
                  : "text-slate-500 hover:text-slate-900 hover:bg-slate-100"
              }`}
              data-testid="toggle-markup"
              title={showMarkup ? "Hide markup — pause drawing" : "Show markup — Shift+drag on Working pane to add areas"}
            >
              <Pencil className="h-3 w-3 mr-1" />
              Markup{markupBoxes.length > 0 ? ` (${markupBoxes.length})` : ""}
            </Button>
          )}

          {/* Phase 64: "Set as Best Photo" quick action (only when the
              current photo isn't already the best in its group). Warns
              that switching the best pick clears any markups already
              drawn — since markups are keyed to the photo they were
              drawn on, promoting a different photo means the editor
              should mark up the NEW pick from scratch. */}
          {onSetBestPhoto && !photo.is_best_shot && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onSetBestPhoto(photo)}
              className="h-7 px-2 rounded-sm text-[10px] text-amber-600 hover:bg-amber-50"
              data-testid="quick-set-best"
              title="Promote this photo to Best of the group. Any markups already drawn will be cleared — confirm before apply."
            >
              <Star className="h-3 w-3 mr-1 fill-current" />
              Best Photo
            </Button>
          )}

          {/* Phase 64: compact slider strip — swapped in from inside
              the Working pane. Keeps the live-preview immediacy but
              in a spot that no longer floats over the image. */}
          <div
            className="flex items-center gap-2 bg-white border border-slate-200 rounded-sm h-7 px-2"
            data-testid="working-quick-adjust"
            onMouseDown={(e) => e.stopPropagation()}
            onDoubleClick={(e) => e.stopPropagation()}
            title="Quick adjust — live preview while marking up."
          >
            {[
              { key: "brightness", label: "B", min: 0.5, max: 1.5, step: 0.01, fmt: (v) => `${Math.round(v * 100)}%` },
              { key: "exposure",   label: "E", min: -1.0, max: 1.0, step: 0.05, fmt: (v) => `${v >= 0 ? "+" : ""}${v.toFixed(1)}` },
              { key: "sharpness",  label: "S", min: 0.5, max: 2.5, step: 0.05, fmt: (v) => v.toFixed(1) },
            ].map((s) => (
              <div key={s.key} className="flex items-center gap-1" data-testid={`working-slider-${s.key}`}>
                <span className="text-[9px] font-bold text-slate-500 w-3" title={s.key}>
                  {s.label}
                </span>
                <input
                  type="range"
                  min={s.min}
                  max={s.max}
                  step={s.step}
                  value={adj[s.key]}
                  onChange={(e) => setAdj({ ...adj, [s.key]: parseFloat(e.target.value) })}
                  className="w-14 accent-slate-800 cursor-pointer"
                />
                <span className="text-[9px] font-mono text-slate-700 w-8 text-right tabular-nums">
                  {s.fmt(adj[s.key])}
                </span>
              </div>
            ))}
            <button
              type="button"
              onClick={() => setAdj(autoAdj)}
              className="text-[9px] font-bold uppercase tracking-widest text-slate-500 hover:text-slate-900 px-1 py-0.5 rounded hover:bg-slate-100"
              data-testid="working-quick-adjust-reset"
              title="Auto = photo-adaptive values computed from this shot's luminance + sharpness"
            >
              Auto
            </button>
            {/* Phase 66: "Natural" preset — a mild, universally-flattering
                pull-down + micro-sharpen. Doesn't peek at the photo metrics
                so it's predictable across a shoot. Complementary to Auto:
                use Auto when this one photo is over/under exposed, use
                Natural when the whole shoot looks fine and you just want
                a gentle everyday touch. */}
            <button
              type="button"
              onClick={() => setAdj({
                brightness: 1.0,
                // Slight pull-down: −0.15 EV avoids the "digital look",
                // matches how most calibrated monitors read as "natural".
                exposure: -0.15,
                // Micro-sharpen: 1.1× — visible on skin & eyelashes but
                // never crunchy on foliage / smooth backgrounds.
                sharpness: 1.1,
              })}
              className="text-[9px] font-bold uppercase tracking-widest text-slate-500 hover:text-slate-900 px-1 py-0.5 rounded hover:bg-slate-100"
              data-testid="working-quick-adjust-natural"
              title="Natural = mild pull-down (-0.15 EV) + micro-sharpen (1.1×). Predictable across a shoot."
            >
              Natural
            </button>
          </div>
          </div>
        </div>

        {/* Side-by-side editor: Original (left) ↔ Working (right) */}
        <div
          ref={containerRef}
          className="relative w-full max-h-[76vh] flex gap-1"
          style={{ cursor: zoom > 1 ? (dragging ? "grabbing" : "grab") : "default" }}
          onWheel={handleWheel}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onDoubleClick={handleDoubleClick}
        >
          {/* Original pane (left) — read-only reference; can cycle through
              sibling photos in the same group via the toolbar. */}
          <div className="flex-1 overflow-hidden rounded-sm border border-slate-200 bg-white relative" data-testid="edit-pane-original">
            <div className="absolute top-2 left-2 z-10 bg-black/70 text-white text-[10px] px-2 py-0.5 rounded-sm font-mono">
              {originalPhoto.id === photo.id ? "Original" : `Group · ${originalPhoto.filename}`}
            </div>
            {originalLoading && (
              <div
                className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-white/70 backdrop-blur-sm gap-2 pointer-events-none"
                data-testid="original-loading"
              >
                <Loader2 className="h-6 w-6 text-primary animate-spin" />
                <p className="text-[10px] uppercase tracking-widest text-slate-600 font-bold">
                  Loading {originalPhoto.filename}…
                </p>
              </div>
            )}
            <div className="flex items-center justify-center overflow-hidden" style={{ maxHeight: "72vh" }}>
              <div style={transformCss} className="relative">
                <img
                  key={originalPhoto.id}
                  src={imgUrl(originalPhoto.id, "fullres")}
                  alt={originalPhoto.filename}
                  className="max-h-[72vh] w-auto select-none block"
                  style={{
                    imageRendering: "high-quality",
                    WebkitImageRendering: "high-quality",
                  }}
                  draggable={false}
                  data-testid="edit-original-image"
                  onLoad={() => setOriginalLoading(false)}
                  onError={() => setOriginalLoading(false)}
                />
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="w-px bg-amber-500 shrink-0 self-stretch" />

          {/* Working pane (right) — editable */}
          <div className="flex-1 overflow-hidden rounded-sm border border-slate-200 bg-white relative" data-testid="edit-pane-working">
            <div className="absolute top-2 right-2 z-10 bg-black/70 text-white text-[10px] px-2 py-0.5 rounded-sm font-mono">
              Working{showMarkup ? " · Markup" : ""}
            </div>
            {/* Phase 64: Working pane filename badge (swapped in from
                the outer toolbar). Editors see immediately which file
                they're marking up without scanning the toolbar. */}
            <div
              className="absolute top-2 left-2 z-10 flex items-center gap-1 bg-white/85 backdrop-blur-sm border border-slate-200 rounded-sm px-2 py-1 shadow-sm max-w-[60%]"
              data-testid="working-filename"
              onMouseDown={(e) => e.stopPropagation()}
              onDoubleClick={(e) => e.stopPropagation()}
            >
              <span
                className="text-[10px] font-mono text-slate-700 truncate"
                title={photo.filename}
              >
                {photo.filename}
              </span>
              <PreviewBadge filename={photo.filename} />
            </div>
            <div className="flex items-center justify-center overflow-hidden" style={{ maxHeight: "72vh" }}>
              <div className="relative inline-block max-h-[72vh]" style={{ ...transformCss, filter: filterCss }}>
                <img
                  ref={workingImgRef}
                  src={imgUrl(photo.id, "fullres")}
                  alt={photo.filename}
                  className={`max-h-[72vh] w-auto select-none block ${
                    showMarkup ? "cursor-crosshair" : ""
                  }`}
                  style={{
                    // Ask the browser for the best available upscaling filter
                    // when the user is zoomed past 100% — otherwise Chrome
                    // falls back to nearest-neighbour at large zooms, which
                    // gives the blocky / blurry appearance the admin flagged.
                    imageRendering: "high-quality",
                    WebkitImageRendering: "high-quality",
                  }}
                  draggable={false}
                  data-testid="layer-base-image"
                  onMouseDown={showMarkup ? mkMouseDown : undefined}
                  onMouseMove={showMarkup ? mkMouseMove : undefined}
                  onMouseUp={showMarkup ? mkMouseUp : undefined}
                  onMouseLeave={showMarkup ? () => { if (mkDrawing) mkMouseUp(); } : undefined}
                />
                {/* Clickable face boxes — only on the Working pane, only when toggled on.
                    `pointer-events-none` on the wrapper so this overlay never
                    intercepts clicks meant for the underlying img (e.g. the
                    Clone tool stamp). Individual face <button>s re-enable
                    pointer events themselves. */}
                <div className="absolute inset-0 pointer-events-none">
                  <ClickableFaceOverlays
                    faces={photo.faces || []}
                    dismissedIdxSet={new Set(photo.dismissed_face_indices || [])}
                    hidden={!showFaceBoxes}
                    issuesOnly={faceBoxMode === "issues"}
                    onPick={(faceIndex, e) => {
                      const rect = containerRef.current?.getBoundingClientRect();
                      setPicker({
                        faceIndex,
                        x: e.clientX - (rect?.left ?? 0),
                        y: e.clientY - (rect?.top ?? 0),
                      });
                    }}
                  />
                </div>

                {/* Markup overlay — on top of everything so admin can
                    annotate the working state. Pointer events pass
                    through when showMarkup is off. */}
                {showMarkup && (
                  <div className="absolute inset-0 pointer-events-none" data-testid="markup-overlay">
                    {markupBoxes.map((b, i) => {
                      const isAuto = b.source === "auto";
                      const active = mkActiveIdx === i;
                      return (
                        <div
                          key={i}
                          onClick={(e) => { e.stopPropagation(); setMkActiveIdx(i); }}
                          className={`absolute pointer-events-auto cursor-pointer transition-colors ${
                            active
                              ? "border-2 border-primary bg-primary/20"
                              : isAuto
                                ? "border-2 border-dashed border-amber-500 bg-amber-500/10 hover:bg-amber-500/20"
                                : "border-2 border-accent bg-accent/10 hover:bg-accent/20"
                          }`}
                          style={{
                            left: `${b.x * 100}%`, top: `${b.y * 100}%`,
                            width: `${b.w * 100}%`, height: `${b.h * 100}%`,
                          }}
                          data-testid={`edit-markup-box-${i}`}
                        >
                          <span
                            className={`absolute -top-6 left-0 text-[10px] font-bold uppercase tracking-widest text-white px-1.5 py-0.5 rounded-sm whitespace-nowrap ${
                              isAuto ? "bg-amber-500" : "bg-accent"
                            }`}
                            style={{ transform: `scale(${1 / zoom})`, transformOrigin: "top left" }}
                          >
                            {isAuto ? "AUTO · " : ""}{b.label || `Area ${i + 1}`}
                          </span>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); mkDelete(i); }}
                            className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-white border border-slate-300 shadow-sm text-slate-600 hover:text-red-600 hover:border-red-400 flex items-center justify-center"
                            title="Remove this markup"
                            style={{ transform: `scale(${1 / zoom})`, transformOrigin: "top right" }}
                            data-testid={`edit-markup-cancel-${i}`}
                          >
                            <XCircle className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      );
                    })}
                    {mkTempBox && (
                      <div
                        className="absolute border-2 border-dashed border-primary bg-primary/10 pointer-events-none"
                        style={{
                          left: `${mkTempBox.x * 100}%`, top: `${mkTempBox.y * 100}%`,
                          width: `${mkTempBox.w * 100}%`, height: `${mkTempBox.h * 100}%`,
                        }}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Dismiss-face popover — clicking a red-flagged face now offers
              a single "Mark acceptable" action that removes the matching
              AUTO markup row (so the editor doesn't get a false positive). */}
          {picker && (() => {
            const face = (photo.faces || [])[picker.faceIndex];
            const isIssue = !!(face?.blink_detected || face?.has_glasses_flare);
            return (
              <div
                className="absolute z-30 bg-white border border-slate-200 rounded-sm shadow-2xl p-2 space-y-1.5 w-56"
                style={{
                  left: Math.min(picker.x, (containerRef.current?.clientWidth || 800) - 240),
                  top: Math.min(picker.y, (containerRef.current?.clientHeight || 500) - 140),
                }}
                data-testid="face-dismiss-picker"
                onMouseDown={(e) => e.stopPropagation()}
              >
                <p className="text-[10px] uppercase tracking-widest text-slate-500 px-1">
                  Face {picker.faceIndex + 1}
                </p>
                {isIssue ? (
                  <>
                    <Button
                      size="sm"
                      onClick={() => acceptAutoForFace(picker.faceIndex)}
                      className="w-full bg-accent text-white hover:bg-accent/90 rounded-sm h-8 text-xs justify-start"
                      data-testid="picker-accept"
                      title="Confirm this face needs fixing — mark as admin-approved for the editor"
                    >
                      <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                      Accept marking (send to editor)
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => removeAutoForFace(picker.faceIndex)}
                      className="w-full border-slate-300 text-slate-700 hover:bg-slate-50 rounded-sm h-8 text-xs justify-start"
                      data-testid="picker-remove"
                      title="This face is fine — remove the marking and don't send to editor"
                    >
                      <XCircle className="h-3.5 w-3.5 mr-1.5" />
                      Remove marking (face is fine)
                    </Button>
                  </>
                ) : (
                  <div className="text-[11px] text-slate-500 px-1 py-2 leading-snug">
                    This face has no auto markup. Use the Markup tool to
                    draw a custom area for the editor.
                  </div>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setPicker(null)}
                  className="w-full text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-sm h-7 text-[10px]"
                >
                  Cancel
                </Button>
              </div>
            );
          })()}
        </div>
        <p className="text-[10px] text-slate-400 text-center">
          {showFaceBoxes
            ? "Click a red-flagged face to mark it acceptable and remove its auto markup"
            : "Face boxes hidden — toggle Faces to enable click-to-dismiss"}
          &nbsp;&middot; Scroll to zoom (up to 2000%) &middot; Drag to pan &middot; Double-click to toggle 3×
        </p>
      </div>

      {/* Bottom: Adjustments + Markup tabs (below the photo now). */}
      <div className="w-full" data-testid="face-layer-panel">
        <Tabs value={sidebarTab} onValueChange={setSidebarTab} className="space-y-3">
          <TabsList className="inline-flex h-8 bg-slate-100 rounded-sm p-0.5">
            <TabsTrigger
              value="adjust"
              className="text-[10px] uppercase tracking-widest data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-sm h-7 px-4"
              data-testid="sidebar-tab-adjust"
            >
              Adjustments
            </TabsTrigger>
            <TabsTrigger
              value="markup"
              className="text-[10px] uppercase tracking-widest data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-sm h-7 px-4"
              data-testid="sidebar-tab-markup"
            >
              Markup {markupBoxes.length > 0 && (
                <span className="ml-1 bg-accent text-white rounded-sm px-1 text-[9px] font-mono">
                  {markupBoxes.length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* ── Adjustments tab ─────────────────────────────────────── */}
          <TabsContent value="adjust" className="space-y-4 mt-0">
        {/* Step 1 — global adjustments */}
        <div className="bg-white border border-slate-200 rounded-sm p-3 space-y-3" data-testid="adjustments-panel">
          <div className="flex items-center gap-2">
            <span className="bg-amber-500 text-black text-[10px] font-mono font-medium px-1.5 py-0.5 rounded-sm">STEP 1</span>
            <h4 className="text-[10px] uppercase tracking-[0.2em] font-medium text-slate-600">
              Adjustments
            </h4>
          </div>
          {[
            { key: "brightness", label: "Brightness", min: 0.5, max: 1.5, step: 0.01, fmt: (v) => `${Math.round(v * 100)}%` },
            { key: "exposure",   label: "Exposure",   min: -1.0, max: 1.0, step: 0.05, fmt: (v) => `${v >= 0 ? "+" : ""}${v.toFixed(2)} EV` },
            { key: "sharpness",  label: "Sharpness",  min: 0.5, max: 2.5, step: 0.05, fmt: (v) => v.toFixed(2) },
          ].map((s) => (
            <div key={s.key} className="space-y-1" data-testid={`slider-${s.key}`}>
              <div className="flex items-center justify-between text-[10px]">
                <span className="text-slate-600">{s.label}</span>
                <span className="font-mono text-slate-900">{s.fmt(adj[s.key])}</span>
              </div>
              <input
                type="range"
                min={s.min}
                max={s.max}
                step={s.step}
                value={adj[s.key]}
                onChange={(e) => setAdj({ ...adj, [s.key]: parseFloat(e.target.value) })}
                className="w-full accent-slate-800"
              />
            </div>
          ))}
          <div className="flex gap-1.5">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setAdj(autoAdj)}
              className="flex-1 h-7 text-[10px] text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-sm"
              data-testid="adjustments-reset"
              title="Reset to auto-fixed values"
            >
              <RotateCcw className="h-3 w-3 mr-1.5" />Auto
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setAdj(ADJ_DEFAULTS)}
              className="flex-1 h-7 text-[10px] text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-sm"
              data-testid="adjustments-neutral"
              title="Reset all sliders to neutral (1.0 / 0 / 1.0)"
            >
              Neutral
            </Button>
          </div>
          <p className="text-[9px] text-slate-400 leading-snug">
            Sliders pre-set to auto-corrected values from the photo&rsquo;s
            brightness &amp; sharpness analysis. Adjustments are preview-only
            &mdash; the download tools have been removed since the workflow
            is now markup-driven (editor downloads originals, fixes offline).
          </p>
        </div>
          </TabsContent>

          {/* ── Markup tab — list of admin-drawn areas for the editor ─── */}
          <TabsContent value="markup" className="mt-0" data-testid="markup-list-panel">
            <MarkupListPanel
              markupBoxes={markupBoxes}
              activeIdx={mkActiveIdx}
              setActiveIdx={setMkActiveIdx}
              showMarkup={showMarkup}
              onToggleMarkup={() => { setShowMarkup((v) => !v); setPicker(null); }}
              onUpdate={(next) => onSaveMarkup && onSaveMarkup(next)}
              onDelete={mkDelete}
              onClearAll={mkClearAll}
              photoFilename={photo.filename}
              onCommitMarkup={onCommitMarkup}
              isCommitted={isCommitted}
              committedInfo={committedInfo}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

/* ── AI Fix progress overlay ───────────────────────────────────── */

const STAGE_META = {
  starting:        { label: "Starting",       color: "text-slate-600" },
  analyzing:       { label: "Analyzing",      color: "text-primary" },
  loading:         { label: "Loading",        color: "text-slate-600" },
  gathering_donors:{ label: "Donors",         color: "text-amber-600" },
  regenerating:    { label: "Regenerating",   color: "text-violet-600" },
  auditing:        { label: "Auditing",       color: "text-yellow-600" },
  compositing:     { label: "Compositing",    color: "text-emerald-500" },
  cleanup:         { label: "Cleanup",        color: "text-slate-600" },
  saving:          { label: "Saving",         color: "text-primary" },
  scoring:         { label: "Scoring",        color: "text-slate-600" },
  done:            { label: "Done",           color: "text-emerald-500" },
  error:           { label: "Error",          color: "text-red-500" },
};

function timeAgo(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms) || ms < 0) return "just now";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s ago`;
}

function AiFixProgressCard({ photo }) {
  const prog = photo.ai_fix_progress || {};
  const meta = STAGE_META[prog.stage] || { label: prog.stage || "Working", color: "text-slate-600" };
  const total = prog.total || 0;
  const cur = prog.current || 0;
  const pct = total ? Math.min(100, Math.round((cur / total) * 100)) : null;
  // Tick the relative time once a second so the "x s ago" stays fresh.
  const [, force] = useState(0);
  useEffect(() => {
    const t = setInterval(() => force((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  return (
    <div
      className="w-80 bg-white border border-slate-200 rounded-sm shadow-lg p-3 space-y-2 animate-fade-up"
      data-testid={`ai-fix-progress-${photo.id}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Loader2 className="h-3.5 w-3.5 text-violet-600 animate-spin shrink-0" />
          <span className="font-mono text-[10px] text-slate-500 truncate">{photo.filename}</span>
        </div>
        <span className={`text-[10px] uppercase tracking-widest font-medium ${meta.color}`}>
          {meta.label}
        </span>
      </div>

      <p className="text-[11px] text-slate-900 leading-snug" data-testid="ai-fix-progress-message">
        {prog.message || "Working…"}
      </p>

      {pct !== null && (
        <div className="space-y-1">
          <div className="flex justify-between text-[9px] text-slate-500 font-mono">
            <span>face {cur}/{total}</span>
            <span>{pct}%</span>
          </div>
          <div className="h-1 bg-slate-100 rounded-sm overflow-hidden">
            <div
              className="h-full bg-violet-600 transition-all duration-300"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      )}

      <p className="text-[9px] text-slate-400 font-mono">
        Updated {timeAgo(prog.updated_at)}
      </p>
    </div>
  );
}

function AiFixProgressOverlay({ session }) {
  const running = (session?.photos || []).filter(
    (p) => p.ai_fix_status === "processing" && p.ai_fix_progress
  );
  if (running.length === 0) return null;

  return (
    <div
      className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-h-[70vh] overflow-y-auto"
      data-testid="ai-fix-progress-overlay"
    >
      {running.map((p) => (
        <AiFixProgressCard key={p.id} photo={p} />
      ))}
    </div>
  );
}

/* ── Main Dashboard ─────────────────────────────────────────────── */


/* ── Markup Editor: admin draws focus rectangles + notes on a photo ─
 *  - Draw mode is always on (crosshair cursor). Left-click + drag draws.
 *  - Shift + drag pans when zoomed. Scroll wheel zooms.
 *  - Auto-saves after every box add / delete; label + note edits
 *    debounce-save at 600 ms so typing isn't chattery.
 *  - On first open (no saved markups yet), auto-populates from the
 *    analyzer's detected problem faces (blink / glasses flare).
 *  - Coordinates stored as normalized fractions [0,1] of the natural
 *    image dimensions, so they survive zoom / resize / re-crop.
 */
const AUTO_POPULATE_MARK = "__mp_auto__";  // sessionStorage key prefix

function _boxFromFace(face, i, reasons) {
  // bbox_pct is % (0-100) of the natural image; convert to 0-1 fraction.
  const x = face.bbox_pct.x / 100;
  const y = face.bbox_pct.y / 100;
  const w = face.bbox_pct.w / 100;
  const h = face.bbox_pct.h / 100;
  return {
    x: Math.max(0, Math.min(0.99, x)),
    y: Math.max(0, Math.min(0.99, y)),
    w: Math.max(0.01, Math.min(1 - x, w)),
    h: Math.max(0.01, Math.min(1 - y, h)),
    label: reasons[0] || `Face ${i + 1}`,
    note: reasons.length > 1 ? reasons.join(" · ") : "",
    source: "auto",
    face_index: i,
  };
}

function _autoBoxesFromFaces(faces, dismissedIdxSet) {
  // Flag every face the analyzer highlights on the Original view:
  //   • blink_detected  → RED box in the analyzer
  //   • has_glasses_flare → FLARE badge
  // ``dismissedIdxSet`` — face indices the admin previously marked
  // acceptable; they are omitted so the auto markup never comes back.
  if (!faces || !faces.length) return [];
  const out = [];
  const dismissed = dismissedIdxSet || new Set();
  faces.forEach((face, i) => {
    if (!face.bbox_pct) return;
    if (dismissed.has(i)) return;
    const reasons = [];
    if (face.blink_detected) reasons.push("Blink");
    if (face.has_glasses_flare) reasons.push("Glasses flare");
    if (reasons.length > 0) out.push(_boxFromFace(face, i, reasons));
  });
  return out;
}

function MarkupEditor({ photo, initialBoxes, onSave, onClear, readOnly, imgSrc }) {
  const [boxes, setBoxes] = useState(initialBoxes || []);
  const [drawing, setDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState(null);
  const [tempBox, setTempBox] = useState(null);
  const [activeIdx, setActiveIdx] = useState(null);
  const [imgSize, setImgSize] = useState({ w: 0, h: 0 });
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [panning, setPanning] = useState(false);
  const panStart = useRef({ x: 0, y: 0 });
  const wrapRef = useRef();
  const imgRef = useRef();

  // ── Reset editor state ONLY when the photo changes ─────────────
  // Splitting this from the boxes-sync effect below keeps the current
  // zoom / pan intact when the parent updates initialBoxes after an
  // auto-save (which happens every draw / delete). Previously zoom
  // reset to 100 % after every markup change.
  useEffect(() => {
    setActiveIdx(null);
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, [photo.id]);

  // Sync boxes when the parent's initialBoxes reference changes,
  // and auto-populate from detected problem faces on first open.
  useEffect(() => {
    setBoxes(initialBoxes || []);
    if (readOnly) return;
    if ((initialBoxes || []).length > 0) return;
    const key = `${AUTO_POPULATE_MARK}${photo.id}`;
    if (sessionStorage.getItem(key)) return;
    const auto = _autoBoxesFromFaces(photo.faces);
    if (auto.length === 0) return;
    sessionStorage.setItem(key, "1");
    setBoxes(auto);
    onSave(auto);
  }, [initialBoxes, photo.id, readOnly]);

  // ── Image sizing / offset (rendered vs natural) ───────────────────
  const onLoadImg = (e) => {
    const el = e.target;
    setImgSize({ w: el.clientWidth, h: el.clientHeight });
  };

  useEffect(() => {
    if (!imgRef.current) return undefined;
    const ro = new ResizeObserver((entries) => {
      for (const en of entries) setImgSize({ w: en.target.clientWidth, h: en.target.clientHeight });
    });
    ro.observe(imgRef.current);
    return () => ro.disconnect();
  }, [imgSrc]);

  // ── Debounced auto-save (600 ms) for label / note edits ──────────
  const saveTimerRef = useRef(null);
  const scheduleAutoSave = (nextBoxes) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      onSave(nextBoxes);
      saveTimerRef.current = null;
    }, 600);
  };
  useEffect(() => () => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
  }, []);

  // ── Zoom + pan ────────────────────────────────────────────────────
  const stepFor = (z) => (z >= 4 ? 1 : 0.5);
  const zoomIn = () => setZoom((z) => Math.min(6, z + stepFor(z)));
  const zoomOut = () => setZoom((z) => {
    const n = Math.max(1, z - stepFor(z));
    if (n === 1) setPan({ x: 0, y: 0 });
    return n;
  });
  const resetZoom = () => { setZoom(1); setPan({ x: 0, y: 0 }); };

  const handleWheel = (e) => {
    e.preventDefault();
    const factor = e.deltaY > 0 ? 1 / 1.15 : 1.15;
    setZoom((z) => {
      const n = Math.max(1, Math.min(6, z * factor));
      if (n === 1) setPan({ x: 0, y: 0 });
      return n;
    });
  };

  // ── Draw + pan mouse routing ─────────────────────────────────────
  // drawStart / tempBox are stored in NORMALIZED fractions [0,1] of
  // the natural image so drawing works correctly at any zoom level —
  // e.clientX comes back in on-screen pixels which are already scaled,
  // so dividing by the *scaled* rect width converts cleanly to a
  // fraction. Rendering multiplies back by the unscaled `imgSize` so
  // the temp box sits inside the same transformed layer as the img.
  const isPanGesture = (e) => zoom > 1 && !e.shiftKey;

  const mouseDown = (e) => {
    if (readOnly || e.button !== 0 || !imgRef.current) return;
    // Phase 67: plain drag pans; SHIFT+drag draws markup. Matches the
    // compare-view convention so users don't learn two gestures.
    if (isPanGesture(e)) {
      setPanning(true);
      panStart.current = { x: e.clientX - pan.x, y: e.clientY - pan.y };
      e.preventDefault();
      return;
    }
    // Drawing branch — require SHIFT so a stray plain-drag while
    // zoomed doesn't accidentally start a new box.
    if (!e.shiftKey) return;
    const ir = imgRef.current.getBoundingClientRect();
    if (ir.width <= 0 || ir.height <= 0) return;
    const nx = (e.clientX - ir.left) / ir.width;
    const ny = (e.clientY - ir.top) / ir.height;
    if (nx < 0 || nx > 1 || ny < 0 || ny > 1) return;
    setDrawing(true);
    setDrawStart({ nx, ny });
    setTempBox({ x: nx, y: ny, w: 0, h: 0 });
    setActiveIdx(null);
    e.preventDefault();
  };

  const mouseMove = (e) => {
    if (panning) {
      setPan({ x: e.clientX - panStart.current.x, y: e.clientY - panStart.current.y });
      return;
    }
    if (!drawing || !drawStart || !imgRef.current) return;
    const ir = imgRef.current.getBoundingClientRect();
    if (ir.width <= 0 || ir.height <= 0) return;
    const nx = Math.max(0, Math.min(1, (e.clientX - ir.left) / ir.width));
    const ny = Math.max(0, Math.min(1, (e.clientY - ir.top) / ir.height));
    setTempBox({
      x: Math.min(drawStart.nx, nx),
      y: Math.min(drawStart.ny, ny),
      w: Math.abs(nx - drawStart.nx),
      h: Math.abs(ny - drawStart.ny),
    });
  };

  const mouseUp = (e) => {
    if (panning) { setPanning(false); return; }
    if (!drawing || !drawStart || !imgRef.current) return;
    setDrawing(false);
    const finalBox = tempBox;
    setDrawStart(null); setTempBox(null);
    if (!finalBox || finalBox.w < 0.005 || finalBox.h < 0.005) return;
    const next = [
      ...boxes,
      {
        x: finalBox.x, y: finalBox.y, w: finalBox.w, h: finalBox.h,
        label: `Area ${boxes.length + 1}`,
        note: "",
        source: "manual",
      },
    ];
    setBoxes(next);
    setActiveIdx(next.length - 1);
    // Auto-save immediately after finishing a shape.
    onSave(next);
  };

  const updateBox = (i, patch) => {
    setBoxes((prev) => {
      const next = prev.map((b, idx) => idx === i ? { ...b, ...patch } : b);
      scheduleAutoSave(next);
      return next;
    });
  };

  const deleteBox = (i) => {
    setBoxes((prev) => {
      const next = prev.filter((_, idx) => idx !== i);
      // Auto-save immediately on delete (no debounce — user intent is clear).
      onSave(next);
      return next;
    });
    setActiveIdx(null);
  };

  const handleClearAll = async () => {
    if (!window.confirm("Clear all markups on this photo?")) return;
    setBoxes([]);
    setActiveIdx(null);
    await onClear();
  };

  return (
    <div className="w-full flex flex-col lg:flex-row gap-4 h-full" data-testid="markup-editor">
      <div className="flex-1 min-w-0 flex flex-col">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          {!readOnly && (
            <>
              {/* Zoom controls */}
              <div className="flex items-center gap-1 border border-slate-200 rounded-sm bg-white h-8 px-1"
                   data-testid="markup-zoom-controls">
                <Button
                  size="icon" variant="ghost"
                  onClick={zoomOut}
                  disabled={zoom <= 1}
                  className="h-6 w-6 p-0 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-sm disabled:opacity-30"
                  data-testid="markup-zoom-out"
                >
                  <ZoomOut className="h-3.5 w-3.5" />
                </Button>
                <span className="font-mono text-[10px] text-slate-500 w-10 text-center select-none">
                  {Math.round(zoom * 100)}%
                </span>
                <Button
                  size="icon" variant="ghost"
                  onClick={zoomIn}
                  disabled={zoom >= 6}
                  className="h-6 w-6 p-0 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-sm disabled:opacity-30"
                  data-testid="markup-zoom-in"
                >
                  <ZoomIn className="h-3.5 w-3.5" />
                </Button>
                {zoom > 1 && (
                  <Button
                    size="sm" variant="ghost"
                    onClick={resetZoom}
                    className="h-6 px-1.5 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-sm text-[10px]"
                    data-testid="markup-zoom-reset"
                  >
                    <RotateCcw className="h-3 w-3" />
                  </Button>
                )}
              </div>
              <Button
                size="sm" variant="ghost"
                onClick={handleClearAll}
                disabled={boxes.length === 0}
                className="h-8 rounded-sm text-xs text-slate-500 hover:text-slate-900 hover:bg-slate-100 disabled:opacity-30"
                data-testid="markup-clear"
              >
                <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                Clear all
              </Button>
            </>
          )}
          <span className="text-[10px] uppercase tracking-widest text-slate-500">
            {boxes.length} area{boxes.length === 1 ? "" : "s"} · auto-saves
            {zoom > 1 ? " · shift-drag to pan" : ""}
          </span>
        </div>

        <div
          ref={wrapRef}
          className={`relative flex-1 min-h-[300px] bg-slate-50 border border-slate-200 rounded-sm overflow-hidden flex items-center justify-center ${
            readOnly ? "cursor-default" : (panning ? "cursor-grabbing" : "cursor-crosshair")
          }`}
          onMouseDown={mouseDown}
          onMouseMove={mouseMove}
          onMouseUp={mouseUp}
          onMouseLeave={() => {
            if (drawing) mouseUp({ clientX: 0, clientY: 0 });
            if (panning) setPanning(false);
          }}
          onWheel={handleWheel}
          data-testid="markup-canvas"
        >
          <div
            className="relative"
            style={{
              transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
              transformOrigin: "center center",
              transition: (drawing || panning) ? "none" : "transform 0.15s ease-out",
            }}
          >
            <img
              ref={imgRef}
              src={imgSrc}
              alt={photo.filename}
              onLoad={onLoadImg}
              draggable={false}
              className="max-w-full max-h-[65vh] object-contain select-none block"
            />
            {boxes.map((b, i) => {
              const isAuto = b.source === "auto";
              const active = activeIdx === i;
              return (
                <div
                  key={i}
                  onClick={(e) => { e.stopPropagation(); if (!readOnly) setActiveIdx(i); }}
                  className={`absolute pointer-events-auto cursor-pointer transition-colors ${
                    active
                      ? "border-2 border-primary bg-primary/20"
                      : isAuto
                        ? "border-2 border-dashed border-amber-500 bg-amber-500/10 hover:bg-amber-500/20"
                        : "border-2 border-accent bg-accent/10 hover:bg-accent/20"
                  }`}
                  style={{
                    left: `${b.x * imgSize.w}px`,
                    top: `${b.y * imgSize.h}px`,
                    width: `${b.w * imgSize.w}px`,
                    height: `${b.h * imgSize.h}px`,
                  }}
                  data-testid={`markup-box-${i}`}
                >
                  <span
                    className={`absolute -top-6 left-0 text-[10px] font-bold uppercase tracking-widest text-white px-1.5 py-0.5 rounded-sm whitespace-nowrap ${
                      isAuto ? "bg-amber-500" : "bg-accent"
                    }`}
                    style={{ transform: `scale(${1 / zoom})`, transformOrigin: "top left" }}
                  >
                    {isAuto ? "AUTO · " : ""}{b.label || `Area ${i + 1}`}
                  </span>
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); deleteBox(i); }}
                      className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-white border border-slate-300 shadow-sm text-slate-600 hover:text-red-600 hover:border-red-400 flex items-center justify-center pointer-events-auto"
                      title="Remove this markup"
                      style={{ transform: `scale(${1 / zoom})`, transformOrigin: "top right" }}
                      data-testid={`markup-box-cancel-${i}`}
                    >
                      <XCircle className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              );
            })}
            {tempBox && imgSize.w > 0 && (
              <div
                className="absolute border-2 border-dashed border-primary bg-primary/10 pointer-events-none"
                style={{
                  left: `${tempBox.x * imgSize.w}px`,
                  top: `${tempBox.y * imgSize.h}px`,
                  width: `${tempBox.w * imgSize.w}px`,
                  height: `${tempBox.h * imgSize.h}px`,
                }}
              />
            )}
          </div>
        </div>
      </div>

      <div className="w-full lg:w-72 shrink-0 space-y-2 overflow-y-auto max-h-[65vh]"
           data-testid="markup-list">
        <p className="text-[10px] uppercase tracking-widest text-slate-500 px-1">
          Markup notes ({boxes.length})
        </p>
        {boxes.length === 0 && (
          <p className="text-xs text-slate-500 px-1 leading-relaxed">
            {readOnly
              ? "No admin markups on this photo yet."
              : "Click-drag on the image to add a markup — it saves automatically."}
          </p>
        )}
        {boxes.map((b, i) => {
          const isAuto = b.source === "auto";
          return (
            <div
              key={i}
              onClick={() => setActiveIdx(i)}
              className={`p-2 rounded-sm border cursor-pointer transition-colors ${
                activeIdx === i
                  ? "bg-primary/10 border-primary"
                  : isAuto
                    ? "bg-amber-500/5 border-amber-500/40 hover:bg-amber-500/10"
                    : "bg-slate-50 border-slate-200 hover:bg-slate-100"
              }`}
              data-testid={`markup-item-${i}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-[10px] font-bold uppercase tracking-widest text-white px-1.5 py-0.5 rounded-sm ${
                  isAuto ? "bg-amber-500" : "bg-accent"
                }`}>
                  {isAuto ? "AUTO" : i + 1}
                </span>
                {!readOnly ? (
                  <Input
                    value={b.label}
                    onChange={(e) => updateBox(i, { label: e.target.value })}
                    placeholder="Label"
                    className="h-7 text-xs bg-white border-slate-200 rounded-sm text-slate-900"
                    data-testid={`markup-label-${i}`}
                  />
                ) : (
                  <span className="text-xs font-semibold text-slate-900 truncate">
                    {b.label || `Area ${i + 1}`}
                  </span>
                )}
                {!readOnly && (
                  <Button
                    size="icon" variant="ghost"
                    onClick={(e) => { e.stopPropagation(); deleteBox(i); }}
                    className="h-6 w-6 shrink-0 text-slate-500 hover:text-red-500 hover:bg-red-500/10 rounded-sm"
                    data-testid={`markup-delete-${i}`}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
              </div>
              {!readOnly ? (
                <Textarea
                  value={b.note}
                  onChange={(e) => updateBox(i, { note: e.target.value })}
                  placeholder={isAuto
                    ? "Auto-detected — add a note for the editor…"
                    : "Note for the editor (e.g. remove flare on glasses)…"}
                  rows={2}
                  className="text-xs bg-white border-slate-200 rounded-sm text-slate-900 placeholder:text-slate-400 resize-none"
                  data-testid={`markup-note-${i}`}
                />
              ) : b.note ? (
                <p className="text-xs text-slate-600 leading-relaxed">{b.note}</p>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}


export default function AnalysisDashboard() {
  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const jobId = searchParams.get("job") || null;
  const isAdmin = user?.role === "admin";
  // Fix-faces gating: admins always allowed; standard users must have
  // the `can_fix_faces` flag turned on by an admin.
  const canFixFaces = isAdmin || !!user?.can_fix_faces;
  // Phase 92: full-screen Camera-Raw edit panel state. Opens on top
  // of the compare dialog; closes back to it.
  const [rawEditOpen, setRawEditOpen] = useState(false);
  // Phase 85: per-group upload for editors. ``useUploadManager`` gives
  // us the same multipart-upload pipeline the Job page uses (progress,
  // resume, cancel), so a group-scoped drop mirrors the job-scoped one.
  const { startUpload: startEditedUpload } = useUploadManager();
  const [groupUploadHint, setGroupUploadHint] = useState(null); // {groupId, matched, unmatched}
  const groupUploadInputsRef = useRef({});

  const [session, setSession] = useState(null);
  const [job, setJob] = useState(null);
  const [markups, setMarkups] = useState({}); // filename → boxes[]
  // Phase 61: track committed status per filename so the "Commit markups"
  // button can flip to a "Committed" pill after sign-off.
  const [committed, setCommitted] = useState({}); // filename → {at, by}
  const [loading, setLoading] = useState(true);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [viewMode, setViewMode] = useState("original");
  const [editLoading, setEditLoading] = useState(false);
  const [aiGrouping, setAiGrouping] = useState(false);
  // Phase 71: AI Grouping progress dialog state. Populated by polling
  // /live?ai_grouping_progress while ``aiGrouping`` is true, and left
  // visible on ``status="failed"`` so the user can hit Retry.
  const [groupingProgress, setGroupingProgress] = useState(null);
  const [groupingDialogOpen, setGroupingDialogOpen] = useState(false);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [aiProvider, setAiProvider] = useState("openai");
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const [aiFixing, setAiFixing] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [autoGrouping, setAutoGrouping] = useState(false);
  const autoGroupedRef = useRef(false);

  // Keyboard prev/next inside the Photo Detail dialog. Arrow-Left ⇐
  // navigates to previous rank in the current group; Arrow-Right ⇒ next.
  // Skips when the user is typing in an input/textarea.
  useEffect(() => {
    if (!selectedPhoto || !selectedGroupId) return undefined;
    const onKey = (e) => {
      if (e.target && ["INPUT", "TEXTAREA"].includes(e.target.tagName)) return;
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      const arr = (session?.photos || []).filter((p) => p.group_id === selectedGroupId);
      arr.sort((a, b) => {
        const ar = a.rank ?? 999;
        const br = b.rank ?? 999;
        if (ar !== br) return ar - br;
        return (b.score ?? 0) - (a.score ?? 0);
      });
      const idx = arr.findIndex((p) => p.id === selectedPhoto.id);
      if (idx < 0) return;
      if (e.key === "ArrowLeft" && idx > 0) {
        e.preventDefault();
        setSelectedPhoto(arr[idx - 1]);
      } else if (e.key === "ArrowRight" && idx < arr.length - 1) {
        e.preventDefault();
        setSelectedPhoto(arr[idx + 1]);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedPhoto, selectedGroupId, session]);

  const loadSession = async () => {
    try {
      const { data } = await axios.get(`${API}/sessions/${sessionId}`);
      setSession(data);
      return data;
    } catch {
      toast.error("Failed to load session");
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Job mode: load the linked Job + admin markups so the analyzer can
  // render a Back-to-Job banner and edit markups per photo.
  const loadJobAndMarkups = React.useCallback(async () => {
    if (!jobId) return;
    try {
      const [j, mk] = await Promise.all([
        axios.get(`${API}/jobs/${jobId}`),
        axios.get(`${API}/jobs/${jobId}/markups`),
      ]);
      setJob(j.data);
      const byName = {};
      const cbyName = {};
      for (const m of (mk.data.markups || [])) {
        byName[m.filename] = m.boxes || [];
        if (m.committed) cbyName[m.filename] = { at: m.committed_at, by: m.committed_by };
      }
      setMarkups(byName);
      setCommitted(cbyName);
    } catch {
      /* silent — analyzer still usable without job context */
    }
  }, [jobId]);

  useEffect(() => { loadJobAndMarkups(); }, [loadJobAndMarkups]);

  // Deep-link: when the URL carries ?photo=<filename> (typically from a
  // "Edit in analyzer" click on the Job page), auto-select that photo
  // and switch to the Edit view so the user lands directly on the
  // markup surface for that specific image. Only runs once per photo
  // param so we don't hijack manual navigation.
  const deepLinkPhoto = searchParams.get("photo");
  const deepLinkAppliedRef = useRef(null);
  useEffect(() => {
    if (!deepLinkPhoto || !session?.photos) return;
    if (deepLinkAppliedRef.current === deepLinkPhoto) return;
    const target = session.photos.find(
      (p) => (p.filename || "").toLowerCase() === deepLinkPhoto.toLowerCase(),
    );
    if (target) {
      deepLinkAppliedRef.current = deepLinkPhoto;
      setSelectedPhoto(target);
      setSelectedGroupId(target.group_id || null);
      setViewMode("edit");
    }
  }, [deepLinkPhoto, session]);

  // Save markup boxes for a filename. Called by the Markup tab UI.
  const saveMarkup = async (filename, boxes) => {
    if (!jobId) return;
    try {
      await axios.put(`${API}/jobs/${jobId}/markups/${encodeURIComponent(filename)}`, { boxes });
      setMarkups((prev) => ({ ...prev, [filename]: boxes }));
      // Phase 61: any edit invalidates a prior commit (matches backend).
      setCommitted((prev) => { const n = { ...prev }; delete n[filename]; return n; });
      toast.success("Markup saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save markup");
    }
  };

  const clearMarkup = async (filename) => {
    if (!jobId) return;
    try {
      await axios.delete(`${API}/jobs/${jobId}/markups/${encodeURIComponent(filename)}`);
      setMarkups((prev) => { const n = { ...prev }; delete n[filename]; return n; });
      setCommitted((prev) => { const n = { ...prev }; delete n[filename]; return n; });
      toast.success("Markup cleared");
    } catch (err) {
      if (err?.response?.status !== 404) {
        toast.error(err?.response?.data?.detail || "Failed to clear markup");
      }
    }
  };

  // Phase 61: commit markups — freezes the current areas as
  // "ready for editor" so external retouchers know which photos are
  // signed off. Backend records ``committed_at`` + ``committed_by`` on
  // the markup doc; the frontend flips the button to a green
  // "Committed ✓" pill until the areas are edited again (which
  // automatically clears the commit).
  const commitMarkup = async (filename) => {
    if (!jobId) return;
    try {
      const { data } = await axios.post(
        `${API}/jobs/${jobId}/markups/${encodeURIComponent(filename)}/commit`,
      );
      setCommitted((prev) => ({
        ...prev,
        [filename]: { at: data?.committed_at, by: data?.committed_by },
      }));
      toast.success("Markups committed — editor can pick up this photo now");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Failed to commit markups",
      );
    }
  };

  // Poll a single photo's state until `done` predicate is satisfied or we time out.
  const pollPhotoUntil = async (photoId, done, { intervalMs = 3000, timeoutMs = 300000 } = {}) => {
    const started = Date.now();
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { data } = await axios.get(`${API}/sessions/${sessionId}`);
      setSession(data);
      const p = (data?.photos || []).find((ph) => ph.id === photoId);
      if (p && done(p)) return p;
      if (Date.now() - started > timeoutMs) {
        return p || { id: photoId, edit_status: "error", edit_error: "Client polling timeout" };
      }
      await new Promise((r) => setTimeout(r, intervalMs));
    }
  };

  useEffect(() => {
    let pollTimer = null;
    let mounted = true;

    const poll = async () => {
      const data = await loadSession();
      if (!mounted) return;

      // Phase 80: mirror the session's ai_grouping_progress into the
      // dedicated ``groupingProgress`` state so the inline pipeline
      // stage-2 UI reflects live heartbeats even when the user did
      // NOT click Start on THIS tab (e.g. they navigated away and
      // came back, or grouping was kicked off from another tab).
      const sp = data?.ai_grouping_progress;
      if (sp && (sp.status === "running" || sp.status === "failed" || sp.status === "complete")) {
        setGroupingProgress((prev) => {
          // Never downgrade a "complete" we already saw locally back
          // to a stale "running" the server might still be serving.
          if (prev?.status === "complete" && sp.status !== "complete") return prev;
          return sp;
        });
      }

      // Phase 80: continue polling while ANY of these are in-flight:
      //   • analyzing / importing (existing)
      //   • ai_grouping_progress.status === "running" (server-side task)
      //   • a multi-photo session that hasn't been AI-grouped yet (so
      //     the pipeline stage-2 stays live as the user picks a provider)
      const groupingRunning = sp?.status === "running";
      const alreadyGrouped = !!(
        data?.ai_grouped_at
        || (data?.groups || []).some((g) => (g.photo_count || 0) > 1)
      );
      const awaitingGroupChoice = (
        data?.status === "complete"
        && !alreadyGrouped
        && (data?.photo_count || 0) > 1
      );

      if (
        data
        && (
          data.status === "analyzing"
          || data.status === "importing"
          || groupingRunning
          || awaitingGroupChoice
        )
      ) {
        pollTimer = setTimeout(poll, 2000);
      }
    };

    poll();
    return () => {
      mounted = false;
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAutoEdit = async (photoId) => {
    setEditLoading(true);
    try {
      // Kick off the background job — backend returns immediately
      const { data: kicked } = await axios.post(
        `${API}/photos/${photoId}/auto-edit`,
        {},
        { timeout: 30000 }
      );
      setSelectedPhoto((cur) => (cur && cur.id === photoId ? kicked : cur));
      toast.info("Local Fix running — we'll update when it's done");

      // Poll until edit_status changes
      const finalPhoto = await pollPhotoUntil(
        photoId,
        (p) => p?.edit_status === "complete" || p?.edit_status === "error",
        { intervalMs: 2000, timeoutMs: 180000 }
      );
      setSession((prev) => ({
        ...prev,
        photos: prev.photos.map((p) => (p.id === photoId ? finalPhoto : p)),
      }));
      setSelectedPhoto(finalPhoto);

      if (finalPhoto.edit_status === "complete") {
        setViewMode("compare");
        setDetailsOpen(true);
        toast.success("Local Fix applied — comparing before/after");
      } else {
        toast.error(finalPhoto.edit_error || "Local Fix failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Local Fix failed");
    } finally {
      setEditLoading(false);
    }
  };

  const handleSetBest = async (photoId) => {
    try {
      await axios.post(`${API}/photos/${photoId}/set-best`);
      await loadSession();
      toast.success("Best shot updated");
    } catch {
      toast.error("Failed to update");
    }
  };

  // Phase 68: quick "Set as Best Photo" from the compare toolbar.
  // Rule change: promoting a new Best Photo clears markups on EVERY
  // photo in the same group — not just the target. This guarantees
  // the editor has ONE clean canonical photo per group with a fresh
  // markup slate, so they never accidentally work off stale
  // annotations tied to a previously-preferred shot.
  const handleQuickSetBest = async (targetPhoto) => {
    if (!targetPhoto) return;
    // Find every photo in the same group + count how many currently
    // have markup areas drawn.
    const groupId = targetPhoto.group_id;
    const groupSiblings = (session?.photos || []).filter((p) => p.group_id === groupId);
    const filesWithMarkup = groupSiblings.filter(
      (p) => (markups[p.filename] || []).length > 0,
    );
    const totalAreas = filesWithMarkup.reduce(
      (n, p) => n + (markups[p.filename] || []).length, 0,
    );
    const msg = totalAreas > 0
      ? `Promote "${targetPhoto.filename}" to Best Photo?\n\n`
        + `This will DELETE all ${totalAreas} markup area${totalAreas === 1 ? "" : "s"} `
        + `across ${filesWithMarkup.length} photo${filesWithMarkup.length === 1 ? "" : "s"} `
        + `in this group so the editor only has ONE clean marked-up photo to work from — `
        + `the new Best Photo — with a fresh markup slate.`
      : `Promote "${targetPhoto.filename}" to Best Photo?\n\n`
        + `No existing markups will be affected (none in this group yet).`;
    if (!window.confirm(msg)) return;
    try {
      // Clear markups on EVERY photo in the group that has any.
      // Fire-and-await in parallel — none of these depend on each
      // other so we don't need serial ordering.
      if (filesWithMarkup.length > 0) {
        await Promise.all(
          filesWithMarkup.map((p) => clearMarkup(p.filename)),
        );
      }
      await axios.post(`${API}/photos/${targetPhoto.id}/set-best`);
      await loadSession();
      toast.success(
        totalAreas > 0
          ? `Best Photo set to ${targetPhoto.filename} — cleared ${totalAreas} markup area${totalAreas === 1 ? "" : "s"}`
          : `Best Photo set to ${targetPhoto.filename}`,
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update Best Photo");
    }
  };

  const handleAiRegroup = async () => {
    // Phase 73: guard against clobbering existing manual work.
    // If the session already has real groups (multi-photo groups)
    // OR the admin has committed / drafted markups on any photo,
    // AI Regroup will destroy the current group_id → photo mapping,
    // reset every Best pick, and re-rank from scratch — which in
    // turn wipes any markups drawn on photos whose Best status
    // changes. Warn explicitly before continuing.
    const hasRealGroups = (session?.groups || []).some(
      (g) => (g.photo_count || 0) > 1,
    );
    const markupCount = Object.values(markups || {}).reduce(
      (acc, boxes) => acc + (boxes?.length || 0), 0,
    );
    const committedCount = Object.keys(committed || {}).length;
    if (hasRealGroups || markupCount > 0 || committedCount > 0) {
      const lines = [
        "Re-run AI Grouping on this session?",
        "",
        "You will LOSE the following existing work:",
      ];
      if (hasRealGroups) {
        lines.push(`  • ${session.group_count} existing group${session.group_count === 1 ? "" : "s"} + Best-photo picks`);
      }
      if (markupCount > 0) {
        lines.push(`  • ${markupCount} markup area${markupCount === 1 ? "" : "s"} drawn across ${Object.keys(markups).length} photo${Object.keys(markups).length === 1 ? "" : "s"}`);
      }
      if (committedCount > 0) {
        lines.push(`  • ${committedCount} committed markup handoff${committedCount === 1 ? "" : "s"} to the editor`);
      }
      lines.push("");
      lines.push("This cannot be undone. Continue?");
      if (!window.confirm(lines.join("\n"))) {
        return;
      }
    }

    // Phase 71: open the progress dialog immediately so the user sees
    // visual feedback even before the first /live poll returns. The
    // dialog will render "Starting…" until the backend writes the
    // first heartbeat (~1 s).
    // Phase 80: for the INITIAL grouping run (no groups exist yet)
    // the inline pipeline card handles progress, so we skip the
    // modal — otherwise both the pipeline card and the modal
    // would show the same info stacked on top of each other. For
    // RE-grouping from the dashboard (real groups already exist),
    // the modal is still the right UX because the user is on the
    // group grid and needs a blocking overlay.
    const _pipelineActive = !(
      session?.ai_grouped_at
      || (session?.groups || []).some((g) => (g.photo_count || 0) > 1)
    );
    setGroupingProgress({
      status: "running",
      current_window: 0,
      total_windows: 0,
      phase: "Starting AI grouping…",
      provider: aiProvider,
    });
    if (!_pipelineActive) {
      setGroupingDialogOpen(true);
    }
    setAiGrouping(true);

    // Phase 74: the backend now runs AI grouping as a background
    // task and returns 202 immediately. Progress + terminal state
    // is delivered exclusively via the /live poll below — the POST
    // itself just needs to be dispatched. This side-steps the
    // Cloudflare / k8s ingress ~60 s idle drop that was killing
    // long grouping runs on production ("Network Error").
    let posted = false;
    try {
      await axios.post(
        `${API}/sessions/${sessionId}/ai-regroup?provider=${aiProvider}`,
        {},
        { timeout: 30000 },
      );
      posted = true;
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Failed to start AI grouping";
      setGroupingProgress({
        status: "failed",
        phase: msg,
        error: msg,
        provider: aiProvider,
      });
      setAiGrouping(false);
      return;
    }

    // Poll /live every second for the ai_grouping_progress heartbeat.
    // Terminates when status flips to "complete" or "failed" (the
    // backend writes both terminal states into the same doc).
    const pollHandle = { stopped: false };
    const finished = new Promise((resolve) => {
      (async () => {
        // Safety cap — 15 min is way more than any realistic run
        // (155-photo folders finish in <60 s). Prevents a runaway
        // poll if the backend task itself silently dies.
        const deadline = Date.now() + 15 * 60 * 1000;
        while (!pollHandle.stopped) {
          if (Date.now() > deadline) {
            setGroupingProgress({
              status: "failed",
              phase: "Timed out waiting for AI grouping to finish",
              error: "Timed out after 15 minutes",
              provider: aiProvider,
            });
            resolve();
            return;
          }
          try {
            const { data } = await axios.get(
              `${API}/sessions/${sessionId}/live`,
              { timeout: 5000 },
            );
            const p = data?.ai_grouping_progress;
            if (p) {
              setGroupingProgress(p);
              if (p.status === "complete") {
                toast.success(
                  `AI grouped into ${p.final_group_count || "?"} groups`,
                );
                await loadSession();
                setTimeout(() => setGroupingDialogOpen(false), 900);
                resolve();
                return;
              }
              if (p.status === "failed") {
                // Dialog stays open on failure — modal has Retry.
                resolve();
                return;
              }
            }
          } catch (_e) { /* transient network — retry */ }
          await new Promise((r) => setTimeout(r, 1000));
        }
        resolve();
      })();
    });

    try {
      await finished;
    } finally {
      pollHandle.stopped = true;
      setAiGrouping(false);
    }
    if (!posted) return;  // unreachable — kept for lint clarity
  };

  const handleReanalyze = async () => {
    if (reanalyzing) return;

    // Phase 73: Re-score updates every photo's score and cascades into
    // group Best picks (rank 1 may shift). Warn if the admin has
    // committed markups — those markups are keyed to a specific photo
    // whose Best status could flip, which would leave the editor with
    // markups on a photo that is no longer the group's Best pick.
    const markupCount = Object.values(markups || {}).reduce(
      (acc, boxes) => acc + (boxes?.length || 0), 0,
    );
    const committedCount = Object.keys(committed || {}).length;
    if (markupCount > 0 || committedCount > 0) {
      const lines = [
        "Re-score every photo in this session?",
        "",
        "Re-scoring recalculates ranks — the Best photo in a group may change.",
        "That MAY invalidate:",
      ];
      if (markupCount > 0) {
        lines.push(`  • ${markupCount} markup area${markupCount === 1 ? "" : "s"} drawn across ${Object.keys(markups).length} photo${Object.keys(markups).length === 1 ? "" : "s"}`);
      }
      if (committedCount > 0) {
        lines.push(`  • ${committedCount} committed markup handoff${committedCount === 1 ? "" : "s"} to the editor`);
      }
      lines.push("");
      lines.push("Groups themselves are preserved. Continue?");
      if (!window.confirm(lines.join("\n"))) {
        return;
      }
    }

    setReanalyzing(true);
    try {
      const { data } = await axios.post(
        `${API}/sessions/${sessionId}/reanalyze`,
        {},
        { timeout: 30000 },
      );
      toast.info(
        `Re-analyzing ${data.photo_count} photo(s) with the latest scoring — rankings will refresh.`,
      );
      // Poll the session every 5 s until every photo has the new fields
      const start = Date.now();
      const timeout = 5 * 60 * 1000;
      const tick = async () => {
        try {
          const { data: fresh } = await axios.get(`${API}/sessions/${sessionId}`);
          setSession(fresh);
          const ready = (fresh.photos || []).every(
            (p) => p.silly_count !== undefined && p.bad_posture_count !== undefined,
          );
          if (ready) {
            toast.success("Re-analysis complete — rankings refreshed.");
            setReanalyzing(false);
            return;
          }
          if (Date.now() - start > timeout) {
            setReanalyzing(false);
            return;
          }
          setTimeout(tick, 5000);
        } catch {
          setReanalyzing(false);
        }
      };
      setTimeout(tick, 5000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start re-analysis");
      setReanalyzing(false);
    }
  };

  const handleAiAnalyze = async (photoId) => {
    setAiAnalyzing(true);
    try {
      const { data } = await axios.post(
        `${API}/photos/${photoId}/ai-analyze`,
        {},
        { timeout: 180000 }
      );
      setSession((prev) => ({
        ...prev,
        photos: prev.photos.map((p) =>
          p.id === photoId ? { ...p, ai_face_analysis: data.ai_analysis } : p
        ),
      }));
      setSelectedPhoto((prev) => prev ? { ...prev, ai_face_analysis: data.ai_analysis } : prev);
      toast.success(`AI analyzed ${data.ai_analysis.length} faces`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI analysis failed");
    } finally {
      setAiAnalyzing(false);
    }
  };

  const handleAiFix = async (photoId) => {
    setAiFixing(true);
    try {
      const { data: kicked } = await axios.post(
        `${API}/photos/${photoId}/ai-fix`,
        {},
        { timeout: 30000 }
      );
      setSelectedPhoto((cur) => (cur && cur.id === photoId ? kicked : cur));
      toast.info("AI Fix running — this can take a few minutes. We'll update when it's done.");

      const finalPhoto = await pollPhotoUntil(
        photoId,
        (p) => p?.ai_fix_status === "complete" || p?.ai_fix_status === "error",
        { intervalMs: 3000, timeoutMs: 1800000 } // up to 30 min
      );
      setSession((prev) => ({
        ...prev,
        photos: prev.photos.map((p) => (p.id === photoId ? finalPhoto : p)),
      }));
      setSelectedPhoto(finalPhoto);

      if (finalPhoto.ai_fix_status === "complete") {
        setViewMode("compare");
        setDetailsOpen(true);
        toast.success("AI Fix applied — comparing before/after");
      } else {
        toast.error(finalPhoto.ai_fix_error || "AI Fix failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "AI Fix failed");
    } finally {
      setAiFixing(false);
    }
  };

  // ── Settings (informational only; keys live server-side) ───────
  const [showSettings, setShowSettings] = useState(false);

  // ── Drag-and-drop between groups ────────────────────────────────
  // Users can grab any photo card in the grouped view and drop it on
  // another group's card to move it there. Uses the native HTML5
  // dataTransfer API — no external DnD lib required. State-heavy
  // parts (source photo id + hovered group id) live in refs / a
  // single useState so the hundreds of thumbnail buttons don't each
  // subscribe to every change.
  const [dragOverGroupId, setDragOverGroupId] = useState(null);
  const draggingRef = useRef(null);   // {photoId, sourceGroupId}

  const handlePhotoDragStart = (e, photo, sourceGroupId) => {
    draggingRef.current = { photoId: photo.id, sourceGroupId };
    e.dataTransfer.effectAllowed = "move";
    // Some browsers cancel the drag unless data is set explicitly.
    try {
      e.dataTransfer.setData("text/plain", photo.id);
    } catch {
      /* ignore */
    }
  };
  const handleGroupDragOver = (e, gid) => {
    if (!draggingRef.current) return;
    if (draggingRef.current.sourceGroupId === gid) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverGroupId !== gid) setDragOverGroupId(gid);
  };
  const handleGroupDragLeave = (e, gid) => {
    // Only clear if the pointer actually leaves the group card, not
    // when it moves onto a child element inside it.
    if (e.currentTarget.contains(e.relatedTarget)) return;
    if (dragOverGroupId === gid) setDragOverGroupId(null);
  };
  const handleGroupDrop = async (e, targetGroupId) => {
    e.preventDefault();
    setDragOverGroupId(null);
    const drag = draggingRef.current;
    draggingRef.current = null;
    if (!drag || drag.sourceGroupId === targetGroupId) return;
    const { photoId, sourceGroupId } = drag;
    // Optimistic UI update first.
    setSession((prev) => {
      if (!prev) return prev;
      const nextPhotos = prev.photos.map((p) =>
        (p.id === photoId ? { ...p, group_id: targetGroupId, is_best_shot: false } : p),
      );
      const sourceRemaining = nextPhotos.filter(
        (p) => p.group_id === sourceGroupId,
      ).length;
      let nextGroups = prev.groups.map((g) => {
        if (g.id === sourceGroupId) return { ...g, photo_count: sourceRemaining };
        if (g.id === targetGroupId) {
          return {
            ...g,
            photo_count: nextPhotos.filter((p) => p.group_id === targetGroupId).length,
          };
        }
        return g;
      });
      // Drop empty source group so the UI doesn't render a ghost card.
      if (sourceRemaining === 0) {
        nextGroups = nextGroups.filter((g) => g.id !== sourceGroupId);
      }
      return {
        ...prev,
        photos: nextPhotos,
        groups: nextGroups,
        group_count: nextGroups.length,
      };
    });
    try {
      await axios.post(
        `${API}/sessions/${sessionId}/photos/${photoId}/move-group`,
        { target_group_id: targetGroupId },
      );
      // Phase 79: the backend re-ranks BOTH source and destination
      // groups (new arrival can promote/demote existing photos and
      // change ``is_best_shot`` on both sides). Re-fetch the session
      // so the UI reflects the new ranks + best-shot badges.
      try {
        const { data } = await axios.get(`${API}/sessions/${sessionId}`);
        setSession(data);
      } catch {
        /* the optimistic UI is already correct enough to fall back on */
      }
      toast.success("Photo moved — groups re-ranked");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail
          || err?.message
          || "Failed to move photo — reloading session",
      );
      // Revert by re-fetching the session (simplest safe path).
      try {
        const { data } = await axios.get(`${API}/sessions/${sessionId}`);
        setSession(data);
      } catch {
        /* ignore */
      }
    }
  };

  // Phase 85: per-group upload flow. Editors pick files, we cross-check
  // each filename against the group's photos, warn if any don't match,
  // then hand the batch to ``useUploadManager`` which drives the
  // multipart resume-safe upload with the floating dock's progress UI.
  const handleGroupUploadPick = (groupId, fileList) => {
    if (!jobId) {
      toast.error("Group-scoped upload only works from the job workflow");
      return;
    }
    const arr = Array.from(fileList || []);
    if (!arr.length) return;
    // Filename-match check: an uploaded file "matches" a group when
    // its basename (case-insensitive, stem-only) equals ANY photo's
    // filename stem in that group. RAW → JPEG conversions and vice
    // versa still match because we compare on stem.
    const stemOf = (name) => (name.split("/").pop() || name).replace(/\.[^.]+$/, "").toLowerCase();
    const groupStems = new Set(groupPhotos(groupId).map((p) => stemOf(p.filename || "")));
    const matched = [];
    const unmatched = [];
    for (const f of arr) {
      if (groupStems.has(stemOf(f.name))) matched.push(f.name);
      else unmatched.push(f.name);
    }
    if (unmatched.length) {
      // Prompt fallback — the editor picked files that don't belong to
      // this group. They can either cancel and re-pick, or proceed and
      // upload them to ``edited/`` anyway (they'll surface on the Job
      // page under the standard edited folder).
      const preview = unmatched.slice(0, 4).join(", ")
        + (unmatched.length > 4 ? `, +${unmatched.length - 4} more` : "");
      const ok = window.confirm(
        `${unmatched.length} of the ${arr.length} file${arr.length === 1 ? "" : "s"} you picked don't match any photo in this group:\n\n${preview}\n\n`
        + `Upload them anyway? They'll go to the edited folder and appear on the Job page — but they won't be linked to this group.`,
      );
      if (!ok) return;
    }
    startEditedUpload(jobId, "edited", arr, { jobName: job?.name });
    setGroupUploadHint({
      groupId,
      matched: matched.length,
      unmatched: unmatched.length,
      total: arr.length,
    });
    toast.success(
      `Uploading ${arr.length} file${arr.length === 1 ? "" : "s"}`
      + (matched.length ? ` — ${matched.length} matched this group` : "")
      + (unmatched.length ? ` (${unmatched.length} unmatched)` : ""),
    );
  };

  const openGroupUploadPicker = (groupId) => {
    const el = groupUploadInputsRef.current[groupId];
    if (el) {
      el.value = "";  // allow re-picking the same files
      el.click();
    }
  };

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-56px)] flex items-center justify-center" data-testid="loading-state">
        <Loader2 className="h-8 w-8 text-slate-500 animate-spin" />
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-[calc(100vh-56px)] flex items-center justify-center text-slate-500" data-testid="error-state">
        Session not found
      </div>
    );
  }

  // ── Phase 80: unified 2-stage pipeline gate ──────────────────
  // Show the Analyze → Group pipeline card until BOTH stages
  // complete. This replaces the old "wait for analyze → jump to
  // dashboard → click AI Regroup → wait in a modal" flow with a
  // single page that shows both stages side-by-side and lets the
  // user pick the grouping provider inline. The user can navigate
  // away (back to jobs, upload more, etc.) — grouping keeps
  // running as a backend background task and the pipeline picks
  // up the live heartbeat when they return.
  const _pipelineAlreadyGrouped = !!(
    session.ai_grouped_at
    || (session.groups || []).some((g) => (g.photo_count || 0) > 1)
  );
  const _pipelineAnalyzing = session.status === "analyzing";
  const _pipelineGroupingRunning = groupingProgress?.status === "running";
  const _pipelineAwaitingGroupChoice = (
    session.status === "complete"
    && !_pipelineAlreadyGrouped
    && (session.photo_count || 0) > 1
    && !_pipelineGroupingRunning
  );
  // Phase 90: when the URL carries ``?photo=`` the caller has deep-
  // linked to a specific photo (e.g. "Edit in analyzer" from
  // JobMarkupPanel). Bypass the "pick a grouping provider" pipeline
  // card so the admin lands directly on the group grid + compare
  // dialog. If grouping never ran, the grid shows singleton groups —
  // still lets the admin review the photo and its markups without
  // being forced to fire another AI grouping run.
  const _deepLinkOverride = !!searchParams.get("photo");
  const _showPipeline = (
    !_deepLinkOverride
    && (
      _pipelineAnalyzing
      || _pipelineGroupingRunning
      || _pipelineAwaitingGroupChoice
    )
  );

  // Analyzing / Grouping unified pipeline page.
  if (_showPipeline) {
    const total = session.photo_count || 1;
    const done = session.analyzed_count || 0;
    const analyzePct = Math.min(100, Math.round((done / Math.max(1, total)) * 100));
    const analyzeDone = !_pipelineAnalyzing;

    const gp = groupingProgress || {};
    const gTotal = gp.total_windows || 0;
    const gCur = gp.current_window || 0;
    const groupPct = gTotal
      ? Math.min(100, Math.round((gCur / gTotal) * 100))
      : (_pipelineGroupingRunning ? null : 0);
    const groupDone = gp.status === "complete";
    const groupFailed = gp.status === "failed";

    return (
      <div className="min-h-[calc(100vh-56px)] px-6 py-6" data-testid="pipeline-state">
        {jobId && job && (
          <div
            className="flex items-center gap-3 mb-6 px-4 py-2.5 bg-primary/10 border border-primary/30 rounded-sm"
            data-testid="job-mode-banner"
          >
            <Button
              size="sm" variant="ghost"
              onClick={() => nav(`/jobs/${jobId}`)}
              className="h-8 px-2 text-slate-900 hover:bg-primary/20 rounded-sm"
              data-testid="btn-back-to-job"
            >
              <ArrowLeft className="h-3.5 w-3.5 mr-1.5" />Back to job
            </Button>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-widest text-primary font-bold">
                {_pipelineAnalyzing ? "Analyzing job (in progress)"
                  : _pipelineGroupingRunning ? "Grouping job (in progress)"
                  : "Ready to group"}
              </p>
              <p className="font-heading text-sm font-semibold text-slate-900 truncate">
                {job.name}
              </p>
            </div>
          </div>
        )}
        <div className="max-w-3xl mx-auto space-y-4">
          {/* Stage 1 — Analyze */}
          <div
            className={`border rounded-sm bg-white p-6 transition-colors ${
              analyzeDone ? "border-emerald-500/40" : "border-primary/40"
            }`}
            data-testid="pipeline-stage-analyze"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold ${
                  analyzeDone ? "bg-emerald-500 text-white"
                    : "bg-primary text-primary-foreground"
                }`}>
                  {analyzeDone ? "✓" : "1"}
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Stage 1</p>
                  <h3 className="font-heading text-lg font-medium text-slate-900">Analyze photos</h3>
                </div>
              </div>
              {!analyzeDone && <Loader2 className="h-4 w-4 text-primary animate-spin" />}
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-xs">
                <span className="text-slate-600" data-testid="analyze-progress-text">
                  {done} of {total} photo{total === 1 ? "" : "s"}
                </span>
                <span className="font-mono text-slate-900">{analyzePct}%</span>
              </div>
              <Progress value={analyzePct} className="h-2" />
              {!analyzeDone && (
                <p className="text-[10px] text-slate-500 pt-1">
                  Detecting faces, checking blinks, measuring sharpness…
                </p>
              )}
              {analyzeDone && (
                <p className="text-[10px] text-emerald-600 font-medium pt-1">
                  ✓ {total} photo{total === 1 ? "" : "s"} analyzed
                </p>
              )}
            </div>
          </div>

          {/* Stage 2 — Group */}
          <div
            className={`border rounded-sm bg-white p-6 transition-colors ${
              !analyzeDone ? "border-slate-200 opacity-60"
                : groupDone ? "border-emerald-500/40"
                : groupFailed ? "border-red-500/40"
                : _pipelineGroupingRunning ? "border-primary/40"
                : "border-primary/40"
            }`}
            data-testid="pipeline-stage-group"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold ${
                  !analyzeDone ? "bg-slate-200 text-slate-500"
                    : groupDone ? "bg-emerald-500 text-white"
                    : groupFailed ? "bg-red-500 text-white"
                    : "bg-primary text-primary-foreground"
                }`}>
                  {groupDone ? "✓" : groupFailed ? "!" : "2"}
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Stage 2</p>
                  <h3 className="font-heading text-lg font-medium text-slate-900">Group photos with AI</h3>
                </div>
              </div>
              {_pipelineGroupingRunning && <Loader2 className="h-4 w-4 text-primary animate-spin" />}
            </div>

            {!analyzeDone && (
              <p className="text-xs text-slate-500" data-testid="pipeline-group-waiting">
                Waiting for analysis to finish…
              </p>
            )}

            {analyzeDone && _pipelineGroupingRunning && (
              <div className="space-y-2" data-testid="pipeline-group-running">
                <div className="flex justify-between text-xs">
                  <span className="text-slate-600">
                    {gp.phase || "Grouping…"}
                    {gTotal > 0 && ` (window ${gCur}/${gTotal})`}
                  </span>
                  {groupPct != null && (
                    <span className="font-mono text-slate-900">{groupPct}%</span>
                  )}
                </div>
                <Progress value={groupPct ?? 0} className="h-2" />
                <p className="text-[10px] text-slate-500 pt-1">
                  Provider: <span className="font-mono">{gp.provider || aiProvider}</span>
                  {" · "}You can leave this page — grouping runs in the background.
                </p>
              </div>
            )}

            {analyzeDone && !_pipelineGroupingRunning && !groupDone && (
              <div className="space-y-3" data-testid="pipeline-group-picker">
                <p className="text-xs text-slate-600">
                  Choose which vision AI to run for burst / bracket detection.
                  Grouping runs on the backend — you can navigate away and come back.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    onClick={() => { setAiProvider("openai"); setTimeout(handleAiRegroup, 50); }}
                    disabled={aiGrouping}
                    className="text-left border border-slate-200 hover:border-primary hover:bg-primary/5 rounded-sm p-4 transition-colors disabled:opacity-50"
                    data-testid="pipeline-group-openai"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Wand2 className="h-4 w-4 text-primary" />
                      <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">GPT-5.2</span>
                    </div>
                    <p className="text-sm font-semibold text-slate-900">Use OpenAI</p>
                    <p className="text-[11px] text-slate-500 mt-0.5">Fast, good general accuracy.</p>
                  </button>
                  <button
                    onClick={() => { setAiProvider("gemini"); setTimeout(handleAiRegroup, 50); }}
                    disabled={aiGrouping}
                    className="text-left border border-slate-200 hover:border-primary hover:bg-primary/5 rounded-sm p-4 transition-colors disabled:opacity-50"
                    data-testid="pipeline-group-gemini"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Wand2 className="h-4 w-4 text-violet-600" />
                      <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Gemini 3 Flash</span>
                    </div>
                    <p className="text-sm font-semibold text-slate-900">Use Gemini</p>
                    <p className="text-[11px] text-slate-500 mt-0.5">Slightly slower, stronger head-count discrimination.</p>
                  </button>
                </div>
                {groupFailed && gp.error && (
                  <p className="text-xs text-red-600" data-testid="pipeline-group-error">
                    Last attempt failed: {gp.error}. Pick a provider to retry.
                  </p>
                )}
              </div>
            )}

            {groupDone && (
              <div className="space-y-2" data-testid="pipeline-group-done">
                <p className="text-xs text-emerald-600 font-medium">
                  ✓ Grouped into {gp.final_group_count || session.group_count || "?"} group{(gp.final_group_count || session.group_count) === 1 ? "" : "s"}
                </p>
                <p className="text-[10px] text-slate-500">
                  Loading the group grid…
                </p>
              </div>
            )}
          </div>

          <p className="text-[10px] text-slate-400 text-center pt-2">
            This page is safe to leave. Both stages run on the backend.
          </p>
        </div>
      </div>
    );
  }

  // Importing from Drive — show a progress UI based on session.drive_import.fetched
  if (session.status === "importing") {
    const di = session.drive_import || {};
    const total = di.total_files || session.photo_count || 1;
    const done = di.fetched || 0;
    const pct = Math.round((done / Math.max(1, total)) * 100);
    const failedCount = (di.failed || []).length;
    return (
      <div className="min-h-[calc(100vh-56px)] flex items-center justify-center px-6" data-testid="importing-state">
        <div className="w-full max-w-md text-center space-y-6">
          <Loader2 className="h-10 w-10 text-slate-800 animate-spin mx-auto" />
          <div>
            <h2 className="font-heading text-2xl font-medium text-slate-900 mb-2">
              Importing from Drive
            </h2>
            <p className="text-slate-600 text-sm">
              Fetching <span className="text-slate-900">{di.folder_name || "your folder"}</span> from Google Drive…
            </p>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-xs">
              <span className="text-slate-600">{done} of {total} files</span>
              <span className="font-mono text-slate-900">{pct}%</span>
            </div>
            <Progress value={pct} className="h-2" />
          </div>
          {failedCount > 0 && (
            <p className="text-[10px] text-amber-600" data-testid="drive-failed-count">
              {failedCount} file(s) skipped (unsupported on Drive — re-upload manually).
            </p>
          )}
          <p className="text-[10px] text-slate-400">
            Analysis starts automatically once the import finishes.
          </p>
        </div>
      </div>
    );
  }

  if (session.status === "error") {
    return (
      <div className="min-h-[calc(100vh-56px)] flex items-center justify-center px-6" data-testid="error-state">
        <div className="text-center space-y-3">
          <AlertTriangle className="h-10 w-10 text-red-500 mx-auto" />
          <h2 className="font-heading text-xl text-slate-900">Analysis Failed</h2>
          <p className="text-slate-600 text-sm">{session.error || "An error occurred during analysis"}</p>
        </div>
      </div>
    );
  }

  // Sort group photos by rank (or score desc if rank missing) so the best
  // shot is always first, followed by 2nd best, 3rd best, and so on.
  const groupPhotos = (gid) => {
    const arr = session.photos.filter((p) => p.group_id === gid);
    arr.sort((a, b) => {
      const ar = a.rank ?? 999;
      const br = b.rank ?? 999;
      if (ar !== br) return ar - br;
      return (b.score ?? 0) - (a.score ?? 0);
    });
    return arr;
  };

  // Phase 88: number of markup areas across every photo in a group.
  // Used by the group header to show an "N markups · Edit required"
  // chip so the admin sees the workload at a glance without opening
  // each photo. Zero means either the admin hasn't flagged anything
  // yet or (with ``group.no_edit_required``) explicitly approved
  // the group.
  const groupMarkupCount = (gid) => {
    let n = 0;
    for (const p of groupPhotos(gid)) {
      const boxes = markups[p.filename];
      if (boxes && boxes.length) n += boxes.length;
    }
    return n;
  };

  // Phase 88: flip the group's ``no_edit_required`` flag. When true
  // the group header renders a green "No edit required" pill and
  // (once ranking / grouping is complete) the editor can safely skip
  // it in their batch. Optimistically updates local state so the
  // toggle feels instant; refreshes the session on failure.
  const setGroupEditStatus = async (gid, noEditRequired) => {
    setSession((prev) => {
      if (!prev) return prev;
      const now = new Date().toISOString();
      const nextGroups = (prev.groups || []).map((g) => (
        g.id === gid
          ? {
              ...g,
              no_edit_required: noEditRequired,
              no_edit_required_at: noEditRequired ? now : null,
            }
          : g
      ));
      return { ...prev, groups: nextGroups };
    });
    try {
      await axios.post(
        `${API}/sessions/${sessionId}/groups/${gid}/edit-status`,
        { no_edit_required: noEditRequired },
      );
      toast.success(
        noEditRequired
          ? "Group marked as no edit required"
          : "Group reopened for editing",
      );
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Failed to update group edit status",
      );
      // Revert on failure by refetching.
      try {
        const { data } = await axios.get(`${API}/sessions/${sessionId}`);
        setSession(data);
      } catch { /* ignore */ }
    }
  };

  // Phase 89: photo-level edit approval / reopen. Fires the
  // ``/photos/{id}/approve-edits`` endpoint which requires every
  // markup box on the photo to be ``review_status: "approved"``
  // (unless ``force=true``). Optimistic local update mirrors
  // ``setGroupEditStatus`` above.
  const approvePhotoEdits = async (photoId, force = false) => {
    const nowIso = new Date().toISOString();
    setSession((prev) => {
      if (!prev) return prev;
      const nextPhotos = (prev.photos || []).map((p) =>
        p.id === photoId
          ? { ...p, edits_approved_at: nowIso, edits_approved_by: "admin" }
          : p,
      );
      return { ...prev, photos: nextPhotos };
    });
    try {
      await axios.post(
        `${API}/photos/${photoId}/approve-edits`,
        force ? { force: true } : {},
      );
      toast.success("Edits approved for this photo");
    } catch (err) {
      // Revert optimistic update by refetching.
      try {
        const { data } = await axios.get(`${API}/sessions/${sessionId}`);
        setSession(data);
      } catch { /* ignore */ }
      toast.error(
        err?.response?.data?.detail || "Failed to approve edits — some markups may still be pending",
      );
    }
  };

  const reopenPhotoEdits = async (photoId) => {
    setSession((prev) => {
      if (!prev) return prev;
      const nextPhotos = (prev.photos || []).map((p) =>
        p.id === photoId
          ? { ...p, edits_approved_at: null, edits_approved_by: null }
          : p,
      );
      return { ...prev, photos: nextPhotos };
    });
    try {
      await axios.post(`${API}/photos/${photoId}/reopen-edits`);
      toast.success("Photo edits reopened — admin can review again");
    } catch (err) {
      try {
        const { data } = await axios.get(`${API}/sessions/${sessionId}`);
        setSession(data);
      } catch { /* ignore */ }
      toast.error(err?.response?.data?.detail || "Failed to reopen photo edits");
    }
  };

  // Group rollup: photos that need admin review (editor has uploaded
  // an edited file but the admin hasn't approved yet).
  const groupPendingReviewCount = (gid) => {
    let n = 0;
    for (const p of groupPhotos(gid)) {
      if (p.edited_upload_at && !p.edits_approved_at) n += 1;
    }
    return n;
  };
  const currentFixes =
    viewMode === "edited" && selectedPhoto?.edited_fixes
      ? selectedPhoto.edited_fixes
      : selectedPhoto?.suggested_fixes || [];
  const currentScore =
    viewMode === "edited" && selectedPhoto?.edited_score != null
      ? selectedPhoto.edited_score
      : selectedPhoto?.score;
  const currentAnalysis =
    viewMode === "edited" && selectedPhoto?.edited_analysis
      ? selectedPhoto.edited_analysis
      : selectedPhoto;

  return (
    <div className="min-h-[calc(100vh-56px)]" data-testid="analysis-dashboard">
      {/* Floating progress overlay shown while any AI Fix is running */}
      <AiFixProgressOverlay session={session} />

      {/* Phase 71 — AI Grouping progress dialog. Opens the moment the
          admin clicks "AI Regroup", polls /live for per-window
          heartbeats, and keeps itself open on failure so the user can
          hit Retry. Grouping is a vital step for large folders — a
          silent 30-second spinner was giving the impression it had
          stalled. */}
      <Dialog
        open={groupingDialogOpen}
        onOpenChange={(open) => {
          // Never allow close while grouping is actively running —
          // the user cannot cancel a mid-flight LLM call cleanly and
          // dismissing the dialog would leave them with an
          // out-of-date grid.
          if (!open && groupingProgress?.status === "running") return;
          setGroupingDialogOpen(open);
        }}
      >
        <DialogContent
          className="max-w-md bg-white border-slate-200 rounded-sm"
          data-testid="ai-grouping-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
              <Sparkles
                className={`h-4 w-4 ${
                  groupingProgress?.status === "failed"
                    ? "text-red-500"
                    : groupingProgress?.status === "complete"
                      ? "text-emerald-500"
                      : "text-primary animate-pulse"
                }`}
              />
              {groupingProgress?.status === "failed"
                ? "AI grouping failed"
                : groupingProgress?.status === "complete"
                  ? "AI grouping complete"
                  : "AI grouping in progress"}
            </DialogTitle>
            <DialogDescription className="text-[11px] uppercase tracking-widest font-bold text-slate-500 mt-1">
              {(groupingProgress?.provider || aiProvider) === "openai" ? "GPT-5.2 vision" : "Gemini 3 Flash vision"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2" data-testid="ai-grouping-progress-body">
            {/* Progress bar — window N of M */}
            {groupingProgress?.status !== "failed" && (
              <div>
                <div className="flex items-center justify-between text-[11px] font-semibold text-slate-600 mb-1.5">
                  <span data-testid="ai-grouping-phase">
                    {groupingProgress?.phase || "Preparing…"}
                  </span>
                  {(groupingProgress?.total_windows || 0) > 0 && (
                    <span
                      className="font-mono tabular-nums"
                      data-testid="ai-grouping-window-counter"
                    >
                      {groupingProgress.current_window} / {groupingProgress.total_windows}
                    </span>
                  )}
                </div>
                <div className="h-2 bg-slate-100 rounded-sm overflow-hidden border border-slate-200">
                  <div
                    className={`h-full transition-all duration-300 ${
                      groupingProgress?.status === "complete"
                        ? "bg-emerald-500"
                        : "bg-primary"
                    }`}
                    style={{
                      width: (() => {
                        const s = groupingProgress?.status;
                        if (s === "complete") return "100%";
                        const cw = groupingProgress?.current_window || 0;
                        const tw = groupingProgress?.total_windows || 0;
                        if (tw > 0) return `${Math.min(100, (cw / tw) * 100)}%`;
                        return "8%"; // indeterminate — sliver so it's visible
                      })(),
                    }}
                    data-testid="ai-grouping-progress-bar"
                  />
                </div>
                {(groupingProgress?.total_windows || 0) === 0
                  && groupingProgress?.status === "running" && (
                  <p className="text-[10px] text-slate-400 mt-1.5">
                    Splitting photos into burst windows…
                  </p>
                )}
              </div>
            )}

            {/* Failure state */}
            {groupingProgress?.status === "failed" && (
              <div
                className="rounded-sm border border-red-200 bg-red-50 p-3 space-y-1"
                data-testid="ai-grouping-error"
              >
                <p className="text-xs font-bold uppercase tracking-widest text-red-700">
                  Something went wrong
                </p>
                <p className="text-[13px] text-red-900 break-words">
                  {groupingProgress.error || groupingProgress.phase}
                </p>
                <p className="text-[11px] text-red-700">
                  Grouping is a vital step. Retry to try again — your
                  previous groups (if any) are still safe.
                </p>
              </div>
            )}

            {/* Success snapshot */}
            {groupingProgress?.status === "complete" && (
              <div className="rounded-sm border border-emerald-200 bg-emerald-50 p-3 text-center">
                <p className="text-[11px] uppercase tracking-widest font-bold text-emerald-700">
                  Result
                </p>
                <p className="text-2xl font-black text-emerald-800 my-1 tabular-nums">
                  {groupingProgress.final_group_count || "—"}
                </p>
                <p className="text-[11px] text-emerald-700 font-semibold">
                  group{groupingProgress.final_group_count === 1 ? "" : "s"} identified
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 sm:gap-2 flex-row flex-wrap">
            {groupingProgress?.status === "failed" && (
              <>
                <Button
                  variant="outline"
                  onClick={() => setGroupingDialogOpen(false)}
                  className="border-slate-300 text-slate-700 hover:bg-slate-50 rounded-sm h-9 font-semibold"
                  data-testid="ai-grouping-dismiss"
                >
                  Close
                </Button>
                <Button
                  onClick={handleAiRegroup}
                  className="bg-primary hover:bg-primary/90 text-white rounded-sm h-9 font-bold"
                  data-testid="ai-grouping-retry"
                >
                  <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                  Retry AI grouping
                </Button>
              </>
            )}
            {groupingProgress?.status === "complete" && (
              <Button
                onClick={() => setGroupingDialogOpen(false)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm h-9 font-bold"
                data-testid="ai-grouping-close-complete"
              >
                <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                Done
              </Button>
            )}
            {groupingProgress?.status === "running" && (
              <p className="text-[10px] text-slate-500 italic w-full text-center">
                Running… please keep this tab open.
              </p>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Sticky floating toolbar ───────────────────────────────
          Always pinned to the top so the user can jump back to the
          job (or jobs list) from anywhere in the analyzer without
          scrolling all the way up. Uses backdrop-blur so it floats
          gracefully over any content beneath. */}
      <div
        className="sticky top-0 z-40 bg-white/85 backdrop-blur border-b border-slate-200 shadow-sm"
        data-testid="analyzer-sticky-toolbar"
      >
        <div className="w-[92vw] max-w-[1800px] mx-auto px-2 py-2 flex items-center gap-3">
          <Button
            size="sm" variant="ghost"
            onClick={() => (jobId ? nav(`/jobs/${jobId}`) : nav("/"))}
            className="h-8 px-2 text-slate-700 hover:bg-slate-100 rounded-sm font-semibold"
            data-testid="btn-back-to-job-sticky"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" />
            {jobId ? "Back to job" : "All jobs"}
          </Button>
          <Button
            size="sm" variant="ghost"
            onClick={() => nav("/")}
            className="h-8 px-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-sm text-xs font-semibold hidden md:inline-flex"
            data-testid="btn-jobs-list-sticky"
          >
            All jobs
          </Button>
          <div className="h-4 w-px bg-slate-200 hidden md:block" />
          <p
            className="font-heading text-sm font-bold text-slate-800 truncate flex-1 min-w-0"
            title={session.name}
          >
            {/* Phase 88: strip the trailing "· batch N/M" suffix from
                the sticky header — after batches merge into one
                session the label is stale and confusing. */}
            {(session.name || "").replace(/\s*·\s*batch\s+\d+\/\d+\s*$/i, "")}
          </p>
          <div className="hidden md:flex items-center gap-2 shrink-0">
            <Badge variant="outline" className="border-slate-200 text-slate-600 rounded-sm text-[11px]">
              {session.photo_count} photos
            </Badge>
            <Badge variant="outline" className="border-slate-200 text-slate-600 rounded-sm text-[11px]">
              {session.group_count} group{session.group_count > 1 ? "s" : ""}
            </Badge>
            <DiskUsageBanner compact />
          </div>
          {jobId && job?.status === "ready_for_review" && isAdmin && (
            <Button
              size="sm"
              onClick={() => nav(`/jobs/${jobId}/review`)}
              className="h-8 bg-accent hover:bg-accent/90 text-white rounded-sm text-xs font-semibold shrink-0"
              data-testid="btn-open-review-from-analyzer-sticky"
            >
              3-way review
            </Button>
          )}
        </div>
      </div>

      <div className="w-[92vw] max-w-[1800px] mx-auto py-6">

      {/* Job-mode banner — visible only when analyzer is linked to a Job */}
      {jobId && job && (
        <div
          className="flex items-center gap-3 mb-4 px-4 py-2.5 bg-primary/10 border border-primary/30 rounded-sm animate-fade-up"
          data-testid="job-mode-banner"
        >
          <Button
            size="sm" variant="ghost"
            onClick={() => nav(`/jobs/${jobId}`)}
            className="h-8 px-2 text-slate-900 hover:bg-primary/20 rounded-sm"
            data-testid="btn-back-to-job"
          >
            <ArrowLeft className="h-3.5 w-3.5 mr-1.5" />Back to job
          </Button>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-widest text-primary font-bold">
              Analyzing job
            </p>
            <p className="font-heading text-sm font-semibold text-slate-900 truncate">
              {job.name}
            </p>
          </div>
          {isAdmin && (
            <span className="text-[10px] uppercase tracking-widest text-accent font-bold px-2 py-1 border border-accent/40 rounded-sm">
              Markup mode available
            </span>
          )}
          {job.status === "ready_for_review" && isAdmin && (
            <Button
              size="sm"
              onClick={() => nav(`/jobs/${jobId}/review`)}
              className="h-8 bg-accent hover:bg-accent/90 text-white rounded-sm text-xs"
              data-testid="btn-open-review-from-analyzer"
            >
              Open 3-way review
            </Button>
          )}
        </div>
      )}

      {/* Summary bar */}
      <div className="flex flex-wrap items-center gap-4 mb-8 animate-fade-up">
        <h2 className="font-heading text-2xl sm:text-3xl font-medium text-slate-900 tracking-tight">
          {(session.name || "").replace(/\s*·\s*batch\s+\d+\/\d+\s*$/i, "")}
        </h2>
        <div className="flex items-center gap-3 ml-auto">
          <Badge variant="outline" className="border-slate-200 text-slate-600 rounded-sm">
            {session.photo_count} photos
          </Badge>
          <Badge variant="outline" className="border-slate-200 text-slate-600 rounded-sm">
            {session.group_count} group{session.group_count > 1 ? "s" : ""}
          </Badge>
          {session.grouping_method && (
            <Badge variant="outline" className="border-slate-200 text-slate-500 rounded-sm text-[10px]">
              {session.grouping_method}
            </Badge>
          )}

          {/* AI Grouping controls */}
          <div className="flex items-center gap-1.5 ml-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleReanalyze}
              disabled={reanalyzing}
              className="border-slate-200 text-slate-900 hover:bg-slate-100 rounded-sm h-8 text-xs"
              data-testid="reanalyze-button"
              title="Re-score photos with the latest scoring rules (catches silly faces & bad body posture)"
            >
              {reanalyzing ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  Re-analyzing…
                </>
              ) : (
                <>
                  <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                  Re-score
                </>
              )}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={aiGrouping}
                  className="border-slate-200 text-slate-900 hover:bg-slate-100 rounded-sm h-8 text-xs"
                  data-testid="ai-regroup-button"
                >
                  {aiGrouping ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                      AI Grouping...
                    </>
                  ) : (
                    <>
                      <Wand2 className="h-3.5 w-3.5 mr-1.5" />
                      AI Group
                    </>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-white border-slate-200">
                <DropdownMenuItem
                  onClick={() => { setAiProvider("openai"); setTimeout(handleAiRegroup, 50); }}
                  className="text-xs text-slate-900 hover:bg-slate-100 cursor-pointer"
                  data-testid="ai-group-openai"
                >
                  <span className="font-mono text-[10px] text-slate-500 mr-2 w-12">GPT-5.2</span>
                  OpenAI Vision
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => { setAiProvider("gemini"); setTimeout(handleAiRegroup, 50); }}
                  className="text-xs text-slate-900 hover:bg-slate-100 cursor-pointer"
                  data-testid="ai-group-gemini"
                >
                  <span className="font-mono text-[10px] text-slate-500 mr-2 w-12">Gemini</span>
                  Gemini 3 Flash
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Settings button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowSettings(true)}
              className="h-8 w-8 p-0 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-sm"
              data-testid="settings-button"
            >
              <Settings className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Settings Dialog — provider picker + user-supplied API keys */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-md bg-white border-slate-200 rounded-sm" data-testid="settings-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-slate-900">AI Settings</DialogTitle>
            <DialogDescription className="text-slate-500 text-xs">
              Choose a preferred AI provider and optionally provide your own API keys.
              Leave a key blank to use the built-in fallback (Emergent / server key).
            </DialogDescription>
          </DialogHeader>

          <SettingsBody onClose={() => setShowSettings(false)} />
        </DialogContent>
      </Dialog>

      {/* AI Grouping prompt — shown when grouping hasn't been run yet */}
      {session.grouping_method === "none" && session.photo_count > 1 && (
        <div
          className="mb-6 p-5 border border-amber-500/30 bg-amber-500/5 rounded-sm flex flex-col md:flex-row items-start md:items-center gap-4"
          data-testid="ai-group-banner"
        >
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <Wand2 className="h-4 w-4 text-amber-600" />
              <h3 className="text-sm font-medium text-slate-900">Group your photos with AI</h3>
            </div>
            <p className="text-xs text-slate-600 mt-1">
              {autoGrouping
                ? "Grouping automatically with OpenAI…"
                : "Photos are currently ungrouped. Choose an AI provider to detect burst/bracket shots of the same scene."}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              disabled={aiGrouping || autoGrouping}
              onClick={() => { setAiProvider("openai"); setTimeout(handleAiRegroup, 50); }}
              className="bg-white text-black hover:bg-gray-200 rounded-sm h-8 text-xs"
              data-testid="ai-group-banner-openai"
            >
              {(aiGrouping && aiProvider === "openai") || autoGrouping ? (
                <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Grouping…</>
              ) : (
                <><span className="font-mono text-[10px] mr-1.5">GPT-5.2</span>Use OpenAI</>
              )}
            </Button>
            <Button
              size="sm"
              disabled={aiGrouping || autoGrouping}
              onClick={() => { setAiProvider("gemini"); setTimeout(handleAiRegroup, 50); }}
              className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white rounded-sm h-8 text-xs"
              data-testid="ai-group-banner-gemini"
            >
              {aiGrouping && aiProvider === "gemini" ? (
                <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Grouping…</>
              ) : (
                <><span className="font-mono text-[10px] mr-1.5">Gemini</span>Use Gemini</>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Phase 72: gate the entire group grid on ``ai_grouped_at``.
          Before AI Grouping has been run, showing 156 solo-groups of
          1 photo each (the raw analyzer output) confuses the user
          and adds no value — every group needs to be re-grouped
          anyway. Instead we render a large call-to-action card so
          the user's next action is unambiguous. The Phase 71
          progress dialog handles feedback WHILE grouping runs; on
          success this branch flips to the actual group grid.

          Phase 73: backwards-compat — sessions grouped BEFORE Phase
          72 was deployed won't have ``ai_grouped_at`` set. Treat
          ANY multi-photo group as evidence of an existing AI
          grouping run so we don't force the user through a
          destructive re-group of already-reviewed work. */}
      {(() => {
        const alreadyGrouped = !!(
          session.ai_grouped_at
          || (session.groups || []).some(
            (g) => (g.photo_count || 0) > 1,
          )
        );
        // Phase 90: also bypass this CTA when a ``?photo=`` deep-link
        // is present — the admin explicitly clicked "Edit in analyzer"
        // on a specific photo, so surface the group grid + compare
        // dialog instead of demanding a grouping run first.
        const deepLinkOverride = !!searchParams.get("photo");
        return !alreadyGrouped && session.photo_count > 0 && !deepLinkOverride;
      })() ? (
        <div
          className="my-10 mx-auto max-w-2xl border border-primary/30 bg-white rounded-md p-8 text-center space-y-6"
          data-testid="grouping-required-cta"
        >
          <div className="flex items-center justify-center gap-3">
            <Wand2 className="h-6 w-6 text-primary" />
            <h2 className="text-lg font-bold text-slate-900">
              Identify &amp; group photos
            </h2>
          </div>
          <p className="text-sm text-slate-600 leading-relaxed">
            All <span className="font-bold text-slate-900" data-testid="grouping-cta-photo-count">
              {session.photo_count}
            </span> photo{session.photo_count === 1 ? "" : "s"} have been
            analyzed. Run AI Grouping now so bursts of the same scene
            are gathered together — then you&apos;ll see the ranked group
            grid with your Best picks highlighted.
          </p>
          <div className="flex gap-2 justify-center flex-wrap">
            <Button
              size="lg"
              disabled={aiGrouping || autoGrouping}
              onClick={() => { setAiProvider("openai"); setTimeout(handleAiRegroup, 50); }}
              className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-sm h-10 px-5 font-bold shadow-sm"
              data-testid="grouping-cta-openai"
            >
              <Wand2 className="h-4 w-4 mr-2" />
              <span className="font-mono text-[10px] mr-1.5 opacity-90">GPT-5.2</span>
              Run AI Grouping
            </Button>
            <Button
              size="lg"
              variant="outline"
              disabled={aiGrouping || autoGrouping}
              onClick={() => { setAiProvider("gemini"); setTimeout(handleAiRegroup, 50); }}
              className="border-slate-300 text-slate-700 hover:bg-slate-50 rounded-sm h-10 px-5 font-semibold"
              data-testid="grouping-cta-gemini"
            >
              <span className="font-mono text-[10px] mr-1.5 opacity-90">Gemini</span>
              Use Gemini instead
            </Button>
          </div>
          <p className="text-[11px] text-slate-500 italic">
            Grouping takes ~5-30 seconds depending on how many bursts
            we detect. You&apos;ll see a live progress dialog.
          </p>
        </div>
      ) : (
      /* Groups */
      <div className="space-y-8">
        {session.groups.map((group, gi) => {
          const photos = groupPhotos(group.id);
          const best = photos.find((p) => p.is_best_shot);
          const isDropTarget = dragOverGroupId === group.id;
          return (
            <div
              key={group.id}
              className={`animate-fade-up border bg-white rounded-sm transition-colors ${
                isDropTarget
                  ? "border-primary ring-2 ring-primary/40 bg-primary/5"
                  : "border-slate-200"
              }`}
              style={{
                animationDelay: `${gi * 100}ms`,
                // Phase 80: memory / paint optimisation for large
                // sessions (156 photos × 30+ group cards). Tells the
                // browser it can skip layout + paint for group cards
                // that are far outside the viewport. The
                // ``contain-intrinsic-size`` gives the browser a
                // stable height reservation so the scroll-bar
                // doesn't jump when a card comes back into view.
                contentVisibility: "auto",
                containIntrinsicSize: "1px 520px",
              }}
              data-testid={`group-${group.id}`}
              onDragOver={(e) => handleGroupDragOver(e, group.id)}
              onDragLeave={(e) => handleGroupDragLeave(e, group.id)}
              onDrop={(e) => handleGroupDrop(e, group.id)}
            >
              {/* Group header */}
              <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
                <div className="flex items-center gap-3">
                  <span className="text-xs uppercase tracking-[0.2em] font-medium text-slate-500">
                    Group {gi + 1}
                  </span>
                  <span className="text-xs text-slate-400">|</span>
                  <span className="text-xs text-slate-600">
                    {group.photo_count} photo{group.photo_count > 1 ? "s" : ""}
                  </span>
                  {/* Phase 88 / 89: edit-status pill. Priority:
                        1) admin-only: photos waiting for admin review
                           after editor upload → blue "N ready to review"
                        2) markups drawn → red "N markups · Edit required"
                        3) admin marked no-edit → green "No edit required"
                        4) neither → neutral "Awaiting review"
                     Admin-only toggle button appears next to the pill. */}
                  {(() => {
                    const mCount = groupMarkupCount(group.id);
                    const noEdit = !!group.no_edit_required;
                    const pendingReview = groupPendingReviewCount(group.id);
                    if (isAdmin && pendingReview > 0) {
                      return (
                        <span
                          className="inline-flex items-center gap-1 rounded-sm px-1.5 h-5 text-[10px] font-bold uppercase tracking-widest bg-sky-50 border border-sky-300 text-sky-700"
                          data-testid={`group-ready-to-review-${group.id}`}
                          title={`${pendingReview} edited photo${pendingReview === 1 ? "" : "s"} waiting for admin review`}
                        >
                          ● {pendingReview} ready to review
                        </span>
                      );
                    }
                    if (mCount > 0) {
                      return (
                        <span
                          className="inline-flex items-center gap-1 rounded-sm px-1.5 h-5 text-[10px] font-bold uppercase tracking-widest bg-red-50 border border-red-200 text-red-700"
                          data-testid={`group-edit-required-${group.id}`}
                          title={`${mCount} markup area${mCount === 1 ? "" : "s"} still open across this group`}
                        >
                          ↻ {mCount} edit{mCount === 1 ? "" : "s"} required
                        </span>
                      );
                    }
                    if (noEdit) {
                      return (
                        <span
                          className="inline-flex items-center gap-1 rounded-sm px-1.5 h-5 text-[10px] font-bold uppercase tracking-widest bg-emerald-50 border border-emerald-200 text-emerald-700"
                          data-testid={`group-no-edit-required-${group.id}`}
                          title={group.no_edit_required_at
                            ? `Approved by admin · ${new Date(group.no_edit_required_at).toLocaleString()}`
                            : "Approved by admin"}
                        >
                          ✓ No edit required
                        </span>
                      );
                    }
                    return (
                      <span
                        className="inline-flex items-center gap-1 rounded-sm px-1.5 h-5 text-[10px] font-bold uppercase tracking-widest bg-slate-100 border border-slate-200 text-slate-500"
                        data-testid={`group-awaiting-${group.id}`}
                      >
                        ● Awaiting review
                      </span>
                    );
                  })()}
                  {isDropTarget && (
                    <span className="text-[10px] uppercase tracking-widest font-bold text-primary">
                      Drop to move here
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {isAdmin && groupMarkupCount(group.id) === 0 && (
                    /* Only offer the toggle when there are no markups
                       — while markups exist the workload is real, so
                       "approve" would be confusing. */
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setGroupEditStatus(group.id, !group.no_edit_required);
                      }}
                      className={`inline-flex items-center gap-1 h-7 px-2 rounded-sm border text-[11px] font-bold uppercase tracking-widest transition-colors ${
                        group.no_edit_required
                          ? "border-slate-300 text-slate-700 hover:border-slate-400 hover:bg-slate-50"
                          : "border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                      }`}
                      title={group.no_edit_required
                        ? "Reopen this group so markups can be added"
                        : "Mark this whole group as needing no edits"}
                      data-testid={`group-toggle-no-edit-${group.id}`}
                    >
                      {group.no_edit_required ? "Reopen" : "Approve group"}
                    </button>
                  )}
                  {best && (
                    <div className="flex items-center gap-1.5 text-xs text-emerald-500">
                      <Star className="h-3 w-3" />
                      <span>Best: {best.filename}</span>
                    </div>
                  )}
                  {/* Phase 84: per-group ZIP download for editors. The
                      backend streams RAW + XMP + a _MARKUPS.txt sheet so
                      the editor has everything they need for this
                      group's batch without pulling the entire job. */}
                  <a
                    href={`${API}/sessions/${sessionId}/groups/${group.id}/download`}
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-1 h-7 px-2 rounded-sm border border-slate-300 text-slate-700 text-[11px] font-bold uppercase tracking-widest hover:border-primary hover:text-primary hover:bg-primary/5 transition-colors"
                    title="Download every photo in this group as a ZIP (RAW + XMP + markups)"
                    data-testid={`group-download-${group.id}`}
                    download
                  >
                    <Download className="h-3 w-3" />
                    Group
                  </a>
                  {/* Phase 85: per-group upload — editor drops edited
                      files, we filename-match them against this group
                      and warn on unmatched before firing the multipart
                      upload through ``useUploadManager``. */}
                  {jobId && (
                    <>
                      <input
                        ref={(el) => { groupUploadInputsRef.current[group.id] = el; }}
                        type="file"
                        multiple
                        className="hidden"
                        onChange={(e) => handleGroupUploadPick(group.id, e.target.files)}
                        data-testid={`group-upload-input-${group.id}`}
                      />
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); openGroupUploadPicker(group.id); }}
                        className="inline-flex items-center gap-1 h-7 px-2 rounded-sm border border-slate-300 text-slate-700 text-[11px] font-bold uppercase tracking-widest hover:border-primary hover:text-primary hover:bg-primary/5 transition-colors"
                        title="Upload edited files for this group (auto-matched by filename)"
                        data-testid={`group-upload-${group.id}`}
                      >
                        <Upload className="h-3 w-3" />
                        Edited
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Photo grid — Phase 70: responsive CSS grid instead of
                  flex-wrap with fixed 320 px tiles. Each tile now scales
                  to fill its column, so at wide viewports we get up to
                  4 tiles per row (each ~340 px wide inside a 90 vw
                  container at 1920 px), and at narrow viewports we
                  gracefully drop to 3 / 2 / 1 columns without dead
                  space on the right. */}
              <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {photos.map((photo) => (
                  <button
                    key={photo.id}
                    draggable
                    onDragStart={(e) => handlePhotoDragStart(e, photo, group.id)}
                    onDragEnd={() => { draggingRef.current = null; setDragOverGroupId(null); }}
                    onClick={() => {
                      setSelectedPhoto(photo);
                      setSelectedGroupId(group.id);
                      setViewMode("edit");
                    }}
                    className={`photo-thumb relative w-full aspect-[4/3] rounded-sm overflow-hidden border-2 transition-colors cursor-grab active:cursor-grabbing ${
                      photo.is_best_shot
                        ? "border-emerald-500"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                    data-testid={`photo-thumb-${photo.id}`}
                    title="Click to open · drag to move to another group"
                  >
                    <img
                      src={imgUrl(photo.id)}
                      alt={photo.filename}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                    {/* Overlays — rank badge (1st, 2nd, 3rd, …) on top-left */}
                    {typeof photo.rank === "number" && photo.rank <= 3 && (
                      <div
                        className={`absolute top-2 left-2 text-[11px] font-medium px-2 py-0.5 rounded-sm tracking-wider shadow-sm inline-flex items-center gap-1 ${
                          photo.rank === 1
                            ? "bg-emerald-500 text-black"
                            : photo.rank === 2
                            ? "bg-amber-500 text-black"
                            : "bg-slate-400 text-black"
                        }`}
                        data-testid={`rank-badge-${photo.rank}`}
                        title={
                          photo.rank === 1 && photo.manual_best
                            ? `Best photo (picked by admin${photo.manual_best_at ? " · " + new Date(photo.manual_best_at).toLocaleString() : ""})`
                            : undefined
                        }
                      >
                        {photo.rank === 1 ? "1st BEST"
                          : photo.rank === 2 ? "2nd"
                          : "3rd"}
                        {photo.rank === 1 && photo.manual_best && (
                          <User
                            className="h-3 w-3"
                            data-testid={`rank-badge-user-picked-${photo.id}`}
                            aria-label="Admin-picked best photo"
                          />
                        )}
                      </div>
                    )}
                    {photo.has_blinks && (
                      <div className="absolute top-2 right-2 bg-red-500 text-white text-[11px] px-1.5 py-1 rounded-sm">
                        <EyeOff className="h-3 w-3" />
                      </div>
                    )}
                    {photo.is_raw && (
                      <div className="absolute bottom-0 right-0 bg-amber-500/90 text-black text-[10px] font-mono font-medium px-1.5 py-0.5 rounded-tl-sm">
                        RAW
                      </div>
                    )}
                    {/* Phase 89: review-state ribbon in the top-right
                        of every photo tile. Blue = editor uploaded,
                        admin hasn't approved yet. Green = admin has
                        approved this photo's edits. Offset when a
                        blink badge is also present so they stack. */}
                    {photo.edits_approved_at ? (
                      <div
                        className={`absolute ${photo.has_blinks ? "top-8" : "top-2"} right-2 bg-emerald-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-sm shadow-sm`}
                        data-testid={`photo-tile-approved-${photo.id}`}
                        title={`Edits approved · ${new Date(photo.edits_approved_at).toLocaleString()}`}
                      >
                        ✓
                      </div>
                    ) : photo.edited_upload_at ? (
                      <div
                        className={`absolute ${photo.has_blinks ? "top-8" : "top-2"} right-2 bg-sky-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-sm shadow-sm`}
                        data-testid={`photo-tile-ready-review-${photo.id}`}
                        title={`Editor uploaded · ${new Date(photo.edited_upload_at).toLocaleString()}`}
                      >
                        ●
                      </div>
                    ) : null}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-2.5 py-1.5 flex items-end justify-between gap-2">
                      <span className="font-mono text-[11px] text-white/90 truncate flex items-center gap-1.5">
                        {photo.filename}
                        {isRawFilename(photo.filename) && (
                          <span
                            className="rounded-sm bg-amber-500/90 text-black px-1 py-[1px] text-[8px] font-semibold uppercase tracking-wider shrink-0"
                            title="Preview only — Download Original for editing"
                          >
                            Preview
                          </span>
                        )}
                      </span>
                      <span className="font-mono text-[12px] text-white font-medium shrink-0">
                        {photo.score}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      )}

      {/* ── Photo Detail Dialog ───────────────────────────────── */}
      <Dialog
        open={!!selectedPhoto}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedPhoto(null);
            setSelectedGroupId(null);
          }
        }}
      >
        <DialogContent
          className="!max-w-[95vw] !w-[95vw] !max-h-[95vh] !h-[95vh] overflow-hidden p-0 bg-white border-slate-200 rounded-sm flex flex-col"
          data-testid="photo-detail-dialog"
          // Phase 96: the RAW edit panel is portalled to document.body
          // so it escapes this dialog's pointer-events lockdown. That
          // also means Radix sees every click inside the RawEditPanel
          // as "outside the dialog" and tries to close us — which
          // would drop the selected photo and instantly kill the RAW
          // editor. Ignore any interact-outside whose target lives in
          // the RAW edit panel.
          onPointerDownOutside={(e) => {
            if (e.target?.closest?.("[data-testid='raw-edit-panel']")) {
              e.preventDefault();
            }
          }}
          onInteractOutside={(e) => {
            if (e.target?.closest?.("[data-testid='raw-edit-panel']")) {
              e.preventDefault();
            }
          }}
          onEscapeKeyDown={(e) => {
            // Also, when the RAW editor is open the Esc key belongs
            // to it — don't let it double-close both.
            if (rawEditOpen) e.preventDefault();
          }}
        >
          <DialogHeader className="px-6 pt-5 pb-0 shrink-0">
            <div className="flex items-center gap-3">
              {/* Prev / Next navigation — moves through photos in the
                  current group's ranked order. Disabled at the ends. */}
              {selectedGroupId && (() => {
                const arr = groupPhotos(selectedGroupId);
                const idx = arr.findIndex((p) => p.id === selectedPhoto?.id);
                const prev = idx > 0 ? arr[idx - 1] : null;
                const next = idx >= 0 && idx < arr.length - 1 ? arr[idx + 1] : null;
                return (
                  <div className="flex items-center gap-1" data-testid="photo-nav-arrows">
                    <Button
                      size="icon"
                      variant="ghost"
                      disabled={!prev}
                      onClick={() => prev && setSelectedPhoto(prev)}
                      className="h-8 w-8 rounded-sm border border-slate-200 text-slate-900 disabled:opacity-30"
                      data-testid="photo-nav-prev"
                      title={prev ? `Previous: ${prev.filename}` : "No previous photo"}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-[10px] text-slate-500 font-mono tabular-nums px-1">
                      {idx >= 0 ? `${idx + 1} / ${arr.length}` : ""}
                    </span>
                    <Button
                      size="icon"
                      variant="ghost"
                      disabled={!next}
                      onClick={() => next && setSelectedPhoto(next)}
                      className="h-8 w-8 rounded-sm border border-slate-200 text-slate-900 disabled:opacity-30"
                      data-testid="photo-nav-next"
                      title={next ? `Next: ${next.filename}` : "No next photo"}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                );
              })()}
              <DialogTitle className="font-heading text-lg font-medium text-slate-900">
                {selectedPhoto?.filename}
              </DialogTitle>
              {selectedPhoto?.is_best_shot && (
                <Badge className="bg-emerald-500/20 text-emerald-500 border-emerald-500/40 rounded-sm text-[10px]">
                  Best Shot
                </Badge>
              )}
              {selectedPhoto?.is_raw && (
                <Badge
                  className="bg-amber-500/20 text-amber-600 border-amber-500/40 rounded-sm text-[10px]"
                  title="Preview shown — the original CR2/CR3 is what editors download from below"
                >
                  RAW · Preview shown
                </Badge>
              )}
              {/* Phase 89: photo-level review state. Shows the admin
                  whether THIS photo has a pending edit + gives an
                  Approve / Reopen affordance. */}
              {selectedPhoto?.edits_approved_at ? (
                <Badge
                  className="bg-emerald-500/20 text-emerald-700 border-emerald-500/40 rounded-sm text-[10px]"
                  data-testid="photo-edits-approved-badge"
                  title={`Approved ${new Date(selectedPhoto.edits_approved_at).toLocaleString()}`}
                >
                  ✓ Edits approved
                </Badge>
              ) : selectedPhoto?.edited_upload_at ? (
                <Badge
                  className="bg-sky-500/15 text-sky-700 border-sky-300 rounded-sm text-[10px] font-bold"
                  data-testid="photo-ready-for-review-badge"
                  title={`Editor uploaded on ${new Date(selectedPhoto.edited_upload_at).toLocaleString()}`}
                >
                  ● Ready for review
                </Badge>
              ) : null}
              {/* Phase 88: group-scope edit-status pill + toggle. Same
                  affordance as the group card header — the admin can
                  approve (or reopen) the WHOLE group from inside the
                  side-by-side dialog too, so they don't have to close
                  it to change the group's status. */}
              {selectedGroupId && (() => {
                const g = (session.groups || []).find((x) => x.id === selectedGroupId);
                if (!g) return null;
                const mCount = groupMarkupCount(selectedGroupId);
                const noEdit = !!g.no_edit_required;
                let pill = null;
                if (mCount > 0) {
                  pill = (
                    <Badge
                      className="bg-red-500/15 text-red-700 border-red-300 rounded-sm text-[10px] font-bold"
                      title={`${mCount} markup area${mCount === 1 ? "" : "s"} still open across this group`}
                      data-testid="dialog-group-edit-required"
                    >
                      ↻ Group needs {mCount} edit{mCount === 1 ? "" : "s"}
                    </Badge>
                  );
                } else if (noEdit) {
                  pill = (
                    <Badge
                      className="bg-emerald-500/15 text-emerald-700 border-emerald-300 rounded-sm text-[10px] font-bold"
                      data-testid="dialog-group-no-edit-required"
                    >
                      ✓ Group approved · no edit
                    </Badge>
                  );
                }
                return (
                  <div className="flex items-center gap-2 ml-auto">
                    {pill}
                    {isAdmin && mCount === 0 && (
                      <button
                        type="button"
                        onClick={() => setGroupEditStatus(selectedGroupId, !g.no_edit_required)}
                        className={`inline-flex items-center gap-1 h-7 px-2 rounded-sm border text-[10px] font-bold uppercase tracking-widest transition-colors ${
                          g.no_edit_required
                            ? "border-slate-300 text-slate-700 hover:border-slate-400 hover:bg-slate-50"
                            : "border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                        }`}
                        title={g.no_edit_required
                          ? "Reopen this group so markups can be added"
                          : "Mark this whole group as needing no edits"}
                        data-testid="dialog-toggle-no-edit"
                      >
                        {g.no_edit_required ? "Reopen group" : "Approve group"}
                      </button>
                    )}
                  </div>
                );
              })()}
            </div>
            <DialogDescription className="text-slate-500 text-xs">
              {selectedPhoto?.width} x {selectedPhoto?.height}px
            </DialogDescription>
          </DialogHeader>

          {selectedPhoto && (
            <div className="flex flex-col h-full overflow-hidden">
              {/* Top: Image with overlays */}
              <div className="flex-1 p-5 flex flex-col items-center bg-white min-h-[300px] overflow-auto">
                {/* Controls row: View toggle + Download */}
                <div className="flex items-center gap-3 mb-3 flex-wrap">
                  {/* Phase 92: full-screen Camera-Raw editor. Opens a
                      98vw overlay with real-time sliders that save an
                      Adobe-compatible XMP next to the CR2 on R2. */}
                  {jobId && (
                    <button
                      type="button"
                      onClick={() => setRawEditOpen(true)}
                      className="inline-flex items-center gap-1 h-8 px-3 rounded-sm border border-primary bg-primary text-white text-[11px] font-bold uppercase tracking-widest hover:bg-primary/90 transition-colors"
                      title="Open the XMP Settings Panel (Camera-Raw style)"
                      data-testid="btn-open-raw-edit"
                    >
                      <SlidersHorizontal className="h-3 w-3" />
                      XMP Settings
                    </button>
                  )}
                  {/* Phase 89: photo-level review approval controls.
                      Only rendered for admins on a job-linked photo
                      once the editor has actually uploaded an edit
                      (or the admin has already approved). Puts the
                      Approve / Reopen affordance right at eye level
                      inside the compare view so admin doesn't have
                      to scroll down / hunt for the button. */}
                  {isAdmin && jobId && (selectedPhoto.edited_upload_at || selectedPhoto.edits_approved_at) && (
                    selectedPhoto.edits_approved_at ? (
                      <button
                        type="button"
                        onClick={() => reopenPhotoEdits(selectedPhoto.id)}
                        className="inline-flex items-center gap-1 h-8 px-3 rounded-sm border border-slate-300 text-slate-700 text-[11px] font-bold uppercase tracking-widest hover:border-slate-400 hover:bg-slate-50 transition-colors"
                        title="Reopen this photo's edits so admin can review again"
                        data-testid="btn-reopen-photo-edits"
                      >
                        Reopen edits
                      </button>
                    ) : (() => {
                      // Enable "Approve edits" only when every markup
                      // box has been decided (approved). Rework or
                      // pending boxes block sign-off unless the admin
                      // opts to force via long-press / hold-shift.
                      const boxes = markups[selectedPhoto.filename] || [];
                      const pending = boxes.filter(
                        (b) => (b.review_status || "pending") !== "approved",
                      );
                      const canApprove = pending.length === 0;
                      return (
                        <button
                          type="button"
                          onClick={(e) => {
                            const force = !!e.shiftKey;
                            approvePhotoEdits(selectedPhoto.id, force);
                          }}
                          disabled={!canApprove}
                          className={`inline-flex items-center gap-1 h-8 px-3 rounded-sm border text-[11px] font-bold uppercase tracking-widest transition-colors ${
                            canApprove
                              ? "border-emerald-500 bg-emerald-500 text-white hover:bg-emerald-600"
                              : "border-slate-300 text-slate-400 cursor-not-allowed"
                          }`}
                          title={canApprove
                            ? "Approve this photo's edits (Shift-click to force approve with pending markups)"
                            : `${pending.length} markup area(s) still need admin decision`}
                          data-testid="btn-approve-photo-edits"
                        >
                          ✓ Approve edits
                        </button>
                      );
                    })()
                  )}
                  {/* Standalone "Original" view was removed — clicking a
                      photo now goes straight into Edit which already has
                      the Original + Working side-by-side plus the Markup
                      panel. This TabsList is left rendering just the
                      single Edit tab so it functions as a heading. */}
                  <Tabs value={viewMode} onValueChange={setViewMode}>
                    <TabsList className="bg-white border border-slate-200 rounded-sm h-8">
                      <TabsTrigger
                        value="edit"
                        className="rounded-sm text-xs data-[state=active]:bg-primary/30 data-[state=active]:text-primary"
                        data-testid="view-edit-tab"
                      >
                        <SlidersHorizontal className="h-3 w-3 mr-1" />
                        Edit
                        {jobId && (markups[selectedPhoto.filename] || []).length > 0 && (
                          <span className="ml-1 text-[10px] font-bold bg-accent text-white px-1 rounded-sm">
                            {(markups[selectedPhoto.filename] || []).length}
                          </span>
                        )}
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 px-3 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-sm text-xs"
                    onClick={() => {
                      // Direct navigation lets the browser honour the
                      // server's Content-Disposition — critical because
                      // the endpoint may return a ZIP (RAW + sibling
                      // .xmp) rather than a single image, and we want
                      // the correct .zip / .CR3 filename in the user's
                      // Downloads folder.
                      const url = `${API}/photos/image/${selectedPhoto.id}?type=original&download=true`;
                      const a = document.createElement("a");
                      a.href = url;
                      a.rel = "noopener";
                      // Leave a.download unset — the server chooses
                      // the filename via Content-Disposition.
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      toast.success(
                        selectedPhoto?.is_raw
                          ? `Downloading ${selectedPhoto.filename} + XMP sidecar`
                          : `Downloading ${selectedPhoto.filename}`,
                      );
                    }}
                    data-testid="download-photo-button"
                    title={
                      selectedPhoto?.is_raw
                        ? "Downloads the original RAW file (CR2/CR3/…) bundled with its .xmp sidecar for retouching — not the preview shown above"
                        : "Downloads the original file for retouching"
                    }
                  >
                    <Download className="h-3.5 w-3.5 mr-1.5" />
                    {selectedPhoto?.is_raw ? "Download Original RAW + XMP" : "Download Original"}
                  </Button>

                  {/* Prominent fix buttons */}
                  <div className="flex items-center gap-2 ml-auto">
                    {/* Details trigger — opens the info sheet below */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDetailsOpen(true)}
                      className="h-9 px-3 border-slate-200 text-slate-900 hover:bg-slate-100 rounded-sm text-xs"
                      data-testid="open-details-button"
                    >
                      <SlidersHorizontal className="h-3.5 w-3.5 mr-1.5" />
                      Details
                    </Button>
                  </div>
                </div>

                {/* Image display — Original (read-only) or Edit (compare + face layers + adjustments + integrated markup) */}
                {viewMode === "edit" ? (
                  <FaceLayerEditor
                    photo={selectedPhoto}
                    sessionId={sessionId}
                    imgUrl={imgUrl}
                    canFixFaces={canFixFaces}
                    jobId={jobId}
                    groupPhotos={selectedGroupId ? groupPhotos(selectedGroupId) : []}
                    markupBoxes={markups[selectedPhoto.filename] || []}
                    onSaveMarkup={
                      isAdmin
                        ? (boxes) => saveMarkup(selectedPhoto.filename, boxes)
                        : undefined
                    }
                    onClearMarkup={
                      isAdmin
                        ? () => clearMarkup(selectedPhoto.filename)
                        : undefined
                    }
                    // Phase 61: commit / committed-status wiring.
                    onCommitMarkup={
                      isAdmin
                        ? () => commitMarkup(selectedPhoto.filename)
                        : undefined
                    }
                    isCommitted={!!committed[selectedPhoto.filename]}
                    committedInfo={committed[selectedPhoto.filename]}
                    // Phase 64/72: quick Best Photo toggle. Accepts a
                    // photo arg so both the RIGHT-pane "Best Photo"
                    // button and the LEFT-pane "Make Best" star can
                    // share the same handler.
                    onSetBestPhoto={
                      isAdmin
                        ? (targetPhoto) => handleQuickSetBest(targetPhoto || selectedPhoto)
                        : undefined
                    }
                    refreshPhoto={async () => {
                      const { data } = await axios.get(`${API}/sessions/${sessionId}`);
                      setSession(data);
                      const fresh = data.photos?.find((p) => p.id === selectedPhoto.id);
                      if (fresh) setSelectedPhoto(fresh);
                    }}
                  />
                ) : (
                  <ZoomableImage
                    src={imgUrl(selectedPhoto.id, "original")}
                    alt={selectedPhoto.filename}
                    faces={selectedPhoto.faces}
                  />
                )}
              </div>

              {/* Right-side Sheet: analysis, AI tools, alternatives, XMP */}
              <Sheet open={detailsOpen} onOpenChange={setDetailsOpen}>
                <SheetContent
                  side="right"
                  className="w-full sm:max-w-md bg-white border-l border-slate-200 p-0 flex flex-col"
                  data-testid="photo-details-sheet"
                >
                  <SheetHeader className="px-5 py-4 border-b border-slate-200">
                    <SheetTitle className="font-heading text-base text-slate-900">
                      Analysis &amp; Actions
                    </SheetTitle>
                    <SheetDescription className="text-[11px] text-slate-500">
                      {selectedPhoto.filename}
                    </SheetDescription>
                  </SheetHeader>
                  <ScrollArea className="flex-1 bg-white">
                    <div className="p-5 space-y-5">
                      {/* Score */}
                      <div>
                        <ScoreBar score={currentScore} label="Quality Score" />
                      </div>

                      {/* Stats */}
                      <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-white border border-slate-200 rounded-sm">
                      <div className="flex items-center gap-1.5 mb-1">
                        <Eye className="h-3 w-3 text-slate-500" />
                        <span className="text-[10px] uppercase tracking-widest text-slate-500">
                          Faces
                        </span>
                      </div>
                      <span className="font-mono text-lg text-slate-900" data-testid="face-count">
                        {currentAnalysis?.face_count ?? selectedPhoto.face_count}
                      </span>
                    </div>
                    <div className="p-3 bg-white border border-slate-200 rounded-sm">
                      <div className="flex items-center gap-1.5 mb-1">
                        <EyeOff className="h-3 w-3 text-slate-500" />
                        <span className="text-[10px] uppercase tracking-widest text-slate-500">
                          Blinks
                        </span>
                      </div>
                      <span
                        className={`font-mono text-lg ${
                          (currentAnalysis?.blink_count ?? selectedPhoto.blink_count) > 0
                            ? "text-red-500"
                            : "text-emerald-500"
                        }`}
                        data-testid="blink-count"
                      >
                        {currentAnalysis?.blink_count ?? selectedPhoto.blink_count}
                      </span>
                    </div>
                    <div className="p-3 bg-white border border-slate-200 rounded-sm">
                      <div className="flex items-center gap-1.5 mb-1">
                        <Focus className="h-3 w-3 text-slate-500" />
                        <span className="text-[10px] uppercase tracking-widest text-slate-500">
                          Sharpness
                        </span>
                      </div>
                      <span className="font-mono text-lg text-slate-900" data-testid="sharpness-value">
                        {(currentAnalysis?.overall_sharpness ?? selectedPhoto.overall_sharpness).toFixed(0)}
                      </span>
                    </div>
                    <div className="p-3 bg-white border border-slate-200 rounded-sm">
                      <div className="flex items-center gap-1.5 mb-1">
                        <Sun className="h-3 w-3 text-slate-500" />
                        <span className="text-[10px] uppercase tracking-widest text-slate-500">
                          Brightness
                        </span>
                      </div>
                      <span className="font-mono text-lg text-slate-900" data-testid="brightness-value">
                        {(currentAnalysis?.brightness ?? selectedPhoto.brightness).toFixed(0)}
                      </span>
                    </div>
                  </div>

                  {/* Suggested Fixes */}
                  <div>
                    <h4 className="text-xs uppercase tracking-[0.2em] font-medium text-slate-500 mb-3">
                      Suggested Fixes
                    </h4>
                    <div className="space-y-2" data-testid="suggested-fixes">
                      {currentFixes.map((fix, i) => (
                        <div
                          key={i}
                          className={`flex items-start gap-2 p-2.5 border rounded-sm text-xs ${
                            severityBg[fix.severity] || severityBg.info
                          }`}
                          data-testid={`fix-${i}`}
                        >
                          {fix.severity === "high" && (
                            <AlertTriangle className="h-3.5 w-3.5 text-red-500 mt-0.5 shrink-0" />
                          )}
                          {fix.severity === "medium" && (
                            <AlertTriangle className="h-3.5 w-3.5 text-amber-600 mt-0.5 shrink-0" />
                          )}
                          {fix.severity === "low" && (
                            <Contrast className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
                          )}
                          {fix.severity === "info" && (
                            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 mt-0.5 shrink-0" />
                          )}
                          <span className={severityColor[fix.severity] || "text-slate-600"}>
                            {fix.description}
                          </span>
                          {!fix.auto_fixable && fix.type === "blink" && selectedGroupId && groupPhotos(selectedGroupId).length > 1 ? (
                            <Badge
                              variant="outline"
                              className="ml-auto text-[8px] border-emerald-500/40 text-emerald-500 rounded-sm shrink-0"
                            >
                              Swap Fix
                            </Badge>
                          ) : !fix.auto_fixable ? (
                            <Badge
                              variant="outline"
                              className="ml-auto text-[8px] border-red-500/40 text-red-500 rounded-sm shrink-0"
                            >
                              Manual
                            </Badge>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="space-y-2">
                    {/* Swap log - show what was swapped */}
                    {selectedPhoto.swap_log && selectedPhoto.swap_log.length > 0 && (
                      <div className="space-y-1.5" data-testid="swap-log">
                        {selectedPhoto.swap_log.map((swap, i) => (
                          <div
                            key={i}
                            className="flex items-start gap-2 p-2 bg-emerald-500/10 border border-emerald-500/30 rounded-sm text-xs"
                          >
                            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 mt-0.5 shrink-0" />
                            <span className="text-emerald-500">{swap.description}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {!selectedPhoto.is_best_shot && (
                      <Button
                        onClick={() => handleSetBest(selectedPhoto.id)}
                        variant="outline"
                        className="w-full border-slate-200 text-slate-900 hover:bg-slate-100 hover:text-slate-900 rounded-sm h-9 text-sm"
                        data-testid="set-best-button"
                      >
                        <Star className="h-3.5 w-3.5 mr-2" />
                        Set as Best Shot
                      </Button>
                    )}

                    {/* AI Tools — detect-only (Auto Fix + AI Fix are promoted into the modal header) */}
                    <div className="pt-2 border-t border-slate-200 mt-2 space-y-2">
                      <h4 className="text-[10px] uppercase tracking-[0.2em] font-medium text-slate-500">
                        AI Tools
                      </h4>
                      <Button
                        onClick={() => handleAiAnalyze(selectedPhoto.id)}
                        disabled={aiAnalyzing}
                        variant="outline"
                        className="w-full border-slate-200 text-slate-900 hover:bg-slate-100 rounded-sm h-9 text-sm"
                        data-testid="ai-detect-button"
                      >
                        {aiAnalyzing ? (
                          <><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />Analyzing faces...</>
                        ) : (
                          <><Eye className="h-3.5 w-3.5 mr-2" />AI Detect Eyes & Smiles</>
                        )}
                      </Button>
                      <p className="text-[10px] text-slate-400 leading-relaxed">
                        Use <span className="text-amber-600 font-medium">Local Fix</span> (fast seamlessClone) or
                        <span className="text-violet-600 font-medium"> AI Fix</span> (Gemini regenerates faces) in the top toolbar.
                      </p>
                    </div>

                    {/* AI Face Analysis Results */}
                    {selectedPhoto.ai_face_analysis && selectedPhoto.ai_face_analysis.length > 0 && (
                      <div data-testid="ai-face-analysis">
                        <h4 className="text-xs uppercase tracking-[0.2em] font-medium text-slate-500 mb-3">
                          AI Face Analysis
                        </h4>
                        <div className="space-y-2 max-h-52 overflow-y-auto">
                          {selectedPhoto.ai_face_analysis.map((a, i) => (
                            <div key={i} className="p-2.5 bg-white border border-slate-200 rounded-sm text-xs space-y-1.5">
                              <div className="flex items-center justify-between">
                                <span className="font-medium text-slate-900">Face {a.face_index + 1}</span>
                                <Badge
                                  variant="outline"
                                  className={`text-[9px] rounded-sm ${
                                    a.overall_quality === "good" ? "border-emerald-500/40 text-emerald-500" :
                                    a.overall_quality === "fair" ? "border-amber-500/40 text-amber-600" :
                                    "border-red-500/40 text-red-500"
                                  }`}
                                >
                                  {a.overall_quality}
                                </Badge>
                              </div>
                              <div className="grid grid-cols-2 gap-1.5">
                                <div className="flex items-center gap-1.5">
                                  <Eye className="h-3 w-3 text-slate-500" />
                                  <span className={a.eyes_score < 50 ? "text-red-500" : "text-slate-600"}>
                                    {a.eyes} ({a.eyes_score})
                                  </span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <span className="text-slate-500 text-[10px]">SMILE</span>
                                  <span className={a.smile_score > 60 ? "text-emerald-500" : "text-slate-600"}>
                                    {a.smile} ({a.smile_score})
                                  </span>
                                </div>
                              </div>
                              {a.issues && a.issues.length > 0 && (
                                <div className="text-amber-600 text-[10px]">
                                  {a.issues.join(" / ")}
                                </div>
                              )}
                              {/* Fixable problem badges — only the four types AI Fix targets */}
                              {(a.eyes === "closed" || a.glasses_flare || a.funny_face || a.tongue_out) && (
                                <div className="flex flex-wrap gap-1 pt-1" data-testid={`fix-flags-face-${a.face_index}`}>
                                  {a.eyes === "closed" && (
                                    <Badge variant="outline" className="text-[9px] border-red-500/40 text-red-500 rounded-sm">
                                      eyes closed
                                    </Badge>
                                  )}
                                  {a.glasses_flare && (
                                    <Badge variant="outline" className="text-[9px] border-amber-500/40 text-amber-600 rounded-sm">
                                      glasses flare
                                    </Badge>
                                  )}
                                  {a.tongue_out && (
                                    <Badge variant="outline" className="text-[9px] border-violet-600/40 text-violet-400 rounded-sm">
                                      tongue out
                                    </Badge>
                                  )}
                                  {a.funny_face && (
                                    <Badge variant="outline" className="text-[9px] border-primary/40 text-blue-400 rounded-sm">
                                      funny face
                                    </Badge>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* AI Fix Log */}
                    {selectedPhoto.ai_fix_log && selectedPhoto.ai_fix_log.length > 0 && (
                      <div data-testid="ai-fix-log">
                        {selectedPhoto.ai_fix_log.map((fix, i) => (
                          <div key={i} className="flex items-start gap-2 p-2 bg-violet-600/10 border border-violet-600/30 rounded-sm text-xs mt-1.5">
                            <Wand2 className="h-3.5 w-3.5 text-violet-600 mt-0.5 shrink-0" />
                            <span className="text-violet-400">{fix.description}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* XMP Metadata */}
                  {selectedPhoto?.xmp_data && Object.keys(selectedPhoto.xmp_data).length > 0 && (
                    <div data-testid="xmp-metadata-section">
                      <h4 className="text-xs uppercase tracking-[0.2em] font-medium text-slate-500 mb-3">
                        XMP Metadata
                      </h4>
                      <div className="space-y-1.5 max-h-48 overflow-y-auto">
                        {Object.entries(selectedPhoto.xmp_data).map(([key, val]) => (
                          <div
                            key={key}
                            className="flex items-start gap-2 text-xs p-1.5 bg-white border border-slate-200 rounded-sm"
                          >
                            <span className="font-mono text-slate-500 shrink-0 min-w-[100px]">
                              {XMP_LABELS[key] || key}
                            </span>
                            <span className="text-slate-600 break-all">
                              {formatXmpValue(key, val)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Alternatives */}
                  {selectedGroupId && groupPhotos(selectedGroupId).length > 1 && (
                    <div>
                      <h4 className="text-xs uppercase tracking-[0.2em] font-medium text-slate-500 mb-3">
                        Alternatives in Group
                      </h4>
                      <div className="flex gap-2 flex-wrap">
                        {groupPhotos(selectedGroupId).map((p) => (
                          <button
                            key={p.id}
                            onClick={() => {
                              setSelectedPhoto(p);
                              setViewMode("original");
                            }}
                            className={`w-16 h-12 rounded-sm overflow-hidden border-2 transition-colors ${
                              p.id === selectedPhoto.id
                                ? "border-slate-200"
                                : "border-slate-200 hover:border-slate-300"
                            }`}
                            data-testid={`alt-photo-${p.id}`}
                          >
                            <img
                              src={imgUrl(p.id)}
                              alt={p.filename}
                              className="w-full h-full object-cover"
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                    </div>
                  </ScrollArea>
                </SheetContent>
              </Sheet>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Phase 92: full-screen Camera-Raw editor overlay. Rendered
          OUTSIDE the compare dialog so its own 98vw overlay isn't
          constrained by the dialog's own transform origin. */}
      <RawEditPanel
        photo={selectedPhoto}
        isOpen={rawEditOpen && !!selectedPhoto}
        onClose={() => setRawEditOpen(false)}
        groupSize={
          selectedPhoto
            ? (() => {
                const g = (session.groups || []).find(
                  (x) => x.id === selectedPhoto.group_id,
                );
                if (!g) return 0;
                // Not every code path populates ``photo_count`` — fall
                // back to the actual ``photo_ids`` array length so the
                // Apply-to-Group checkbox always reflects reality.
                return g.photo_count
                  || (Array.isArray(g.photo_ids) ? g.photo_ids.length : 0);
              })()
            : 0
        }
      />
      </div>
    </div>
  );
}